import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import DownHint from '../shared/DownHint';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const Section3Mindmap = ({ id }) => {
  const svgRef = useRef();
  const markdownRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [highlightedSection, setHighlightedSection] = useState('root');
  
  // 新的交互状态
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'focus', 'path'
  const [zoomLevel, setZoomLevel] = useState(1);
  const [currentPath, setCurrentPath] = useState(['数学优化方法的分类']);
  const [focusedNode, setFocusedNode] = useState(null);
  const [visibleNodeTypes, setVisibleNodeTypes] = useState({
    root: true,
    category: true,
    framework: true,
    subframework: true,
    method: true
  });

  // 自定义滚动条状态管理
  const [scrollbarVisible, setScrollbarVisible] = useState(false);
  const hideScrollbarTimeout = useRef(null);

  // 搜索功能
  // Markdown内容搜索辅助函数
  const searchInMarkdown = (query, mindmapData, fullMarkdownContent) => {
    const results = [];
    const lowerQuery = query.toLowerCase();
    
    // 解析markdown内容，提取章节信息
    const lines = fullMarkdownContent.split('\n');
    let currentSection = null;
    let currentContent = '';
    
    lines.forEach((line, index) => {
      // 检测标题行 (### 或 #### 开头，且包含 {#sectionId})
      const headingMatch = line.match(/^(#{3,4})\s+(.+?)\s+\{#([^}]+)\}$/);
      
      if (headingMatch) {
        // 如果之前有内容，先处理之前的章节
        if (currentSection && currentContent.toLowerCase().includes(lowerQuery)) {
          const node = findNodeBySectionId(mindmapData, currentSection.sectionId);
          if (node) {
            // 提取匹配上下文
            const matchIndex = currentContent.toLowerCase().indexOf(lowerQuery);
            const start = Math.max(0, matchIndex - 50);
            const end = Math.min(currentContent.length, matchIndex + query.length + 50);
            const context = currentContent.substring(start, end);
            
            results.push({
              ...node,
              matchType: 'content',
              matchContext: start > 0 ? '...' + context : context,
              sectionTitle: currentSection.title
            });
          }
        }
        
        // 开始新的章节
        const title = headingMatch[2];
        const sectionId = headingMatch[3];
        
        currentSection = { title, sectionId };
        currentContent = '';
        
        // 检查标题是否匹配
        if (title.toLowerCase().includes(lowerQuery)) {
          const node = findNodeBySectionId(mindmapData, sectionId);
          if (node) {
            results.push({
              ...node,
              matchType: 'title',
              matchContext: title,
              sectionTitle: title
            });
          }
        }
      } else {
        // 累积当前章节的内容
        currentContent += line + '\n';
      }
    });
    
    // 处理最后一个章节
    if (currentSection && currentContent.toLowerCase().includes(lowerQuery)) {
      const node = findNodeBySectionId(mindmapData, currentSection.sectionId);
      if (node) {
        const matchIndex = currentContent.toLowerCase().indexOf(lowerQuery);
        const start = Math.max(0, matchIndex - 50);
        const end = Math.min(currentContent.length, matchIndex + query.length + 50);
        const context = currentContent.substring(start, end);
        
        results.push({
          ...node,
          matchType: 'content',
          matchContext: start > 0 ? '...' + context : context,
          sectionTitle: currentSection.title
        });
      }
    }
    
    return results;
  };
  
  // 根据sectionId查找对应的mind节点
  const findNodeBySectionId = (data, sectionId) => {
    if (data.sectionId === sectionId) {
      return data;
    }
    
    if (data.children) {
      for (const child of data.children) {
        const found = findNodeBySectionId(child, sectionId);
        if (found) return found;
      }
    }
    
    return null;
  };
  // 根据sectionId查找节点路径
  const findNodePath = (data, sectionId, currentPath = []) => {
    const newPath = [...currentPath, data.name];
    
    if (data.sectionId === sectionId) {
      return newPath;
    }
    
    if (data.children) {
      for (const child of data.children) {
        const found = findNodePath(child, sectionId, newPath);
        if (found) return found;
      }
    }
    
    return null;
  };
  const searchInNodes = (nodes, query, path = []) => {
    let results = [];
    
    // 原有的mind节点名称搜索逻辑
    nodes.forEach(node => {
      const currentPath = [...path, node.name];
      
      // 检查当前节点是否匹配
      if (node.name.toLowerCase().includes(query.toLowerCase())) {
        results.push({
          ...node,
          path: currentPath,
          pathString: currentPath.join(' > '),
          matchType: 'nodeName'
        });
      }
      
      // 递归搜索子节点
      if (node.children) {
        results = results.concat(searchInNodes(node.children, query, currentPath));
      }
    });
    
    // 新增：搜索markdown内容
    if (path.length === 0) { // 只在顶层调用时搜索markdown
      const markdownResults = searchInMarkdown(query, mindmapData, fullMarkdownContent);
      
      // 为markdown搜索结果添加路径信息
      markdownResults.forEach(markdownResult => {
        const nodePath = findNodePath(mindmapData, markdownResult.sectionId);
        if (nodePath) {
          results.push({
            ...markdownResult,
            path: nodePath,
            pathString: nodePath.join(' > ')
          });
        }
      });
    }
    
    // 去重：如果同一个节点既有节点名称匹配又有markdown匹配，优先保留节点名称匹配
    const uniqueResults = [];
    const seenSectionIds = new Set();
    
    results.forEach(result => {
      const key = result.sectionId;
      if (!seenSectionIds.has(key)) {
        seenSectionIds.add(key);
        uniqueResults.push(result);
      } else if (result.matchType === 'nodeName') {
        // 如果已存在的是markdown匹配，但当前是节点名称匹配，则替换
        const existingIndex = uniqueResults.findIndex(r => r.sectionId === key);
        if (existingIndex !== -1 && uniqueResults[existingIndex].matchType !== 'nodeName') {
          uniqueResults[existingIndex] = result;
        }
      }
    });
    
    return uniqueResults;
  };;

  // 智能排序搜索结果函数
  const sortSearchResults = (results, query) => {
    return results.sort((a, b) => {
      const queryLower = query.toLowerCase();
      const aNameLower = a.name.toLowerCase();
      const bNameLower = b.name.toLowerCase();
      
      // 完全匹配
      if (aNameLower === queryLower) return -1;
      if (bNameLower === queryLower) return 1;
      
      // 开头匹配
      if (aNameLower.startsWith(queryLower) && !bNameLower.startsWith(queryLower)) return -1;
      if (bNameLower.startsWith(queryLower) && !aNameLower.startsWith(queryLower)) return 1;
      
      // 按节点层级排序（更高级的分类在前）
      const typeOrder = { root: 0, category: 1, framework: 2, subframework: 3, method: 4 };
      return (typeOrder[a.type] || 5) - (typeOrder[b.type] || 5);
    });
  };

  // 处理搜索输入
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      const results = searchInNodes([mindmapData], query);
      const sortedResults = sortSearchResults(results, query.trim());
      setSearchResults(sortedResults);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  // 选择搜索结果
  const handleSearchResultSelect = (result) => {
    const focusNodeData = {
      name: result.name,
      sectionId: result.sectionId,
      type: result.type
    };
    
    setCurrentPath(result.path);
    setFocusedNode(focusNodeData);
    // 搜索结果选择时，进入聚焦视图
    setViewMode('focus');
    setShowSearchResults(false);
    setSearchQuery(result.name);
    
    // 滚动到对应的markdown章节
    if (result.sectionId) {
      scrollToSection(result.sectionId);
    }
  };

  // 缩放控制
  const handleZoom = (direction) => {
    if (direction === 'in') {
      setZoomLevel(Math.min(zoomLevel * 1.4, 4));
    } else if (direction === 'out') {
      setZoomLevel(Math.max(zoomLevel / 1.4, 0.3));
    } else if (direction === 'reset') {
      setZoomLevel(1);
      setViewMode('overview');
      setFocusedNode(null);
      setCurrentPath(['数学优化方法的分类']);
    }
  };

  // 搜索按钮触发和回车键触发的搜索逻辑
  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      const results = searchInNodes([mindmapData], searchQuery.trim());
      if (results.length > 0) {
        // 使用智能排序，确保选择真正的最佳匹配项
        const sortedResults = sortSearchResults(results, searchQuery.trim());
        const bestMatch = sortedResults[0];
        handleSearchResultSelect(bestMatch);
      }
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // 处理搜索框焦点事件
  const handleSearchFocus = () => {
    // 如果搜索框有内容，自动显示搜索结果下拉框
    if (searchQuery.trim()) {
      const results = searchInNodes([mindmapData], searchQuery.trim());
      const sortedResults = sortSearchResults(results, searchQuery.trim());
      setSearchResults(sortedResults);
      setShowSearchResults(true);
    }
  };

  // 处理点击外部区域关闭下拉框
  const handleClickOutside = (e) => {
    if (!e.target.closest('.search-container')) {
      setShowSearchResults(false);
    }
  };

  // 添加点击外部区域的事件监听
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 切换节点类型可见性
  const toggleNodeType = (type) => {
    setVisibleNodeTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // 获取节点类型颜色
  const getNodeTypeColor = (type) => {
    switch(type) {
      case "root": return "#3b82f6";           // 蓝色
      case "category": return "#10b981";       // 绿色
      case "framework": return "#f59e0b";      // 橙色
      case "subframework": return "#8b5cf6";   // 紫色
      case "method": return "#ef4444";         // 橘红色
      default: return "#64748b";               // 灰色
    }
  };

  // 根据节点名称查找节点数据
  const findNodeByName = (nodeName, rootNode = mindmapData) => {
    if (rootNode.name === nodeName) {
      return rootNode;
    }
    if (rootNode.children) {
      for (const child of rootNode.children) {
        const result = findNodeByName(nodeName, child);
        if (result) return result;
      }
    }
    return null;
  };

  // 处理路径节点点击事件
  const handlePathNodeClick = (nodeName) => {
    const nodeData = findNodeByName(nodeName);
    if (nodeData) {
      
      const fullNodeData = {
        name: nodeData.name,
        sectionId: nodeData.sectionId,
        type: nodeData.type
      };
      
      setFocusedNode(fullNodeData);
      setViewMode('focus');
      
      // 更新当前路径
      const pathToNode = getPathToNode(fullNodeData);
      setCurrentPath(pathToNode);
      
      // 滚动到对应章节
      if (nodeData.sectionId) {
        scrollToSection(nodeData.sectionId);
      }
    }
  };

  // 获取节点路径的辅助函数
  const getPathToNode = (targetNode) => {
    const findPath = (node, target, currentPath = []) => {
      const newPath = [...currentPath, node.name];
      
      if (node.sectionId === target.sectionId) {
        return newPath;
      }
      
      if (node.children) {
        for (const child of node.children) {
          const path = findPath(child, target, newPath);
          if (path) return path;
        }
      }
      
      return null;
    };
    
    return findPath(mindmapData, targetNode) || [targetNode.name];
  };


  // 添加自定义滚动条样式和数学公式样式 - 只初始化一次
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'custom-scrollbar-style';
    style.textContent = `
      /* 确保KaTeX数学公式正常显示 */
      .katex {
        color: var(--ink-mid) !important;
      }
      
      .katex .base {
        color: var(--ink-mid) !important;
      }
      
      /* 只针对块级数学公式居中显示 */
      .katex-display {
        text-align: center !important;
        margin: 1.5rem 0 !important;
      }
      
      /* 基础滚动条样式 - 默认隐藏状态 */
      .custom-scrollbar::-webkit-scrollbar {
        width: 12px !important;
        background: transparent !important;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent !important;
        border-radius: 6px !important;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: transparent !important;
        border-radius: 6px !important;
        border: 2px solid transparent !important;
        background-clip: content-box !important;
        transition: all 0.3s ease !important;
      }
      
      /* 显示状态的滚动条 - 薄荷绿配色 */
      .custom-scrollbar.scrollbar-visible::-webkit-scrollbar-thumb {
        background: rgba(16, 185, 129, 0.7) !important;
      }
      
      .custom-scrollbar.scrollbar-visible::-webkit-scrollbar-thumb:hover {
        background: rgba(16, 185, 129, 1) !important;
      }
      
      /* Firefox 滚动条支持 */
      .custom-scrollbar {
        scrollbar-width: thin !important;
        scrollbar-color: transparent transparent !important;
      }
      
      .custom-scrollbar.scrollbar-visible {
        scrollbar-color: rgba(16, 185, 129, 0.7) transparent !important;
      }
    `;
    
    // 清理可能存在的旧样式
    const existingStyle = document.getElementById('custom-scrollbar-style');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // 添加新样式
    document.head.appendChild(style);
    
    return () => {
      const styleElement = document.getElementById('custom-scrollbar-style');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []); // 移除scrollbarVisible依赖，只初始化一次

  // 处理滚动条显示逻辑
  useEffect(() => {
    const scrollContainer = markdownRef.current;
    if (!scrollContainer) return;

    let isScrolling = false;
    let scrollTimeout;
    let isMouseInContainer = false;

    const showScrollbar = () => {
      setScrollbarVisible(true);
      if (hideScrollbarTimeout.current) {
        clearTimeout(hideScrollbarTimeout.current);
      }
    };

    const hideScrollbarWithDelay = () => {
      // 只有在鼠标不在容器内且没有滚动时才延迟隐藏
      if (!isScrolling && !isMouseInContainer) {
        hideScrollbarTimeout.current = setTimeout(() => {
          setScrollbarVisible(false);
        }, 350);
      }
    };

    // 监听滚动事件
    const handleScroll = () => {
      isScrolling = true;
      showScrollbar();
      
      // 清除之前的滚动超时
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // 滚动停止后标记为非滚动状态
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
        // 滚动结束后，如果鼠标不在容器内，则延迟隐藏滚动条
        hideScrollbarWithDelay();
      }, 150);
    };

    // 监听鼠标进入markdown容器
    const handleMouseEnter = () => {
      isMouseInContainer = true;
      showScrollbar();
    };

    // 监听鼠标离开markdown容器
    const handleMouseLeave = () => {
      isMouseInContainer = false;
      hideScrollbarWithDelay();
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      if (hideScrollbarTimeout.current) {
        clearTimeout(hideScrollbarTimeout.current);
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  // 思维导图数据，添加sectionId作为定位标识
  const mindmapData = {
    name: "数学优化方法的分类",
    type: "root",
    sectionId: "root",
    children: [
      {
        name: "1. 按决策变量类型",
        type: "category",
        sectionId: "decision-variable-type",
        children: [
          {
            name: "连续优化",
            type: "method",
            sectionId: "decision-variable-type-continuous"
          },
          {
            name: "离散优化",
            type: "method", 
            sectionId: "decision-variable-type-discrete"
          },
          {
            name: "混合整数优化",
            type: "method",
            sectionId: "decision-variable-type-mixed"
          },
          {
            name: "组合优化",
            type: "method",
            sectionId: "decision-variable-type-combinatorial"
          }
        ]
      },
      {
        name: "2. 按约束与可行域结构",
        type: "category",
        sectionId: "constraint-feasible-region",
        children: [
          {
            name: "无约束优化",
            type: "method",
            sectionId: "constraint-unconstrained"
          },
          {
            name: "有约束优化",
            type: "method",
            sectionId: "constraint-constrained"
          }
        ]
      },
      {
        name: "3. 按目标与模型结构",
        type: "category",
        sectionId: "objective-model-structure",
        children: [
          {
            name: "非线性规划",
            type: "framework",
            sectionId: "objective-nlp",
            children: [
              {
                name: "凸优化",
                type: "subframework",
                sectionId: "objective-convex",
                children: [
                  {
                    name: "线性规划",
                    type: "method",
                    sectionId: "objective-lp"
                  },
                  {
                    name: "二次规划",
                    type: "method",
                    sectionId: "objective-qp"
                  },
                  {
                    name: "锥规划",
                    type: "method",
                    sectionId: "objective-conic"
                  },
                  {
                    name: "几何规划",
                    type: "method",
                    sectionId: "objective-gp"
                  }
                ]
              },
              {
                name: "非凸优化",
                type: "subframework",
                sectionId: "objective-nonconvex"
              }
            ]
          },
          {
            name: "特殊结构问题",
            type: "framework",
            sectionId: "objective-special",
            children: [
              {
                name: "非线性最小二乘",
                type: "method",
                sectionId: "objective-nlls"
              },
              {
                name: "复合 / 非光滑优化",
                type: "method",
                sectionId: "objective-composite"
              }
            ]
          }
        ]
      },
      {
        name: "4. 按不确定性与信息结构",
        type: "category",
        sectionId: "uncertainty-information",
        children: [
          {
            name: "确定性优化",
            type: "method",
            sectionId: "uncertainty-deterministic"
          },
          {
            name: "随机优化",
            type: "method",
            sectionId: "uncertainty-stochastic"
          },
          {
            name: "鲁棒优化",
            type: "method",
            sectionId: "uncertainty-robust"
          },
          {
            name: "分布鲁棒优化",
            type: "method",
            sectionId: "uncertainty-dro"
          }
        ]
      },
      {
        name: "5. 按时域与决策流程",
        type: "category",
        sectionId: "time-decision-process",
        children: [
          {
            name: "静态优化",
            type: "method",
            sectionId: "time-static"
          },
          {
            name: "动态优化",
            type: "method",
            sectionId: "time-dynamic"
          },
          {
            name: "动态规划",
            type: "method",
            sectionId: "time-dp"
          },
          {
            name: "在线/流式优化",
            type: "method",
            sectionId: "time-online"
          },
          {
            name: "探索型序贯优化",
            type: "method",
            sectionId: "time-bayesian"
          }
        ]
      },
      {
        name: "6. 按目标个数与解的概念",
        type: "category",
        sectionId: "objective-count-solution",
        children: [
          {
            name: "单目标优化",
            type: "method",
            sectionId: "objective-single"
          },
          {
            name: "多目标优化",
            type: "method",
            sectionId: "objective-multi"
          },
          {
            name: "解的质量层级",
            type: "framework",
            sectionId: "objective-quality",
            children: [
              {
                name: "全局最优解",
                type: "method",
                sectionId: "objective-global"
              },
              {
                name: "局部最优解",
                type: "method",
                sectionId: "objective-local"
              },
              {
                name: "近似最优解",
                type: "method",
                sectionId: "objective-approximate"
              },
              {
                name: "工程可用解",
                type: "method",
                sectionId: "objective-engineering"
              }
            ]
          }
        ]
      },
      {
        name: "7. 按求解策略与算法范式",
        type: "category",
        sectionId: "solving-strategy-algorithm",
        children: [
          {
            name: "目标函数光滑可微",
            type: "framework",
            sectionId: "algorithm-differentiable",
            children: [
              {
                name: "一阶方法",
                type: "method",
                sectionId: "algorithm-first-order"
              },
              {
                name: "二阶与拟二阶方法",
                type: "method",
                sectionId: "algorithm-second-order"
              },
              {
                name: "利用特殊结构的算法",
                type: "method",
                sectionId: "algorithm-structure"
              },
              {
                name: "高阶优化方法",
                type: "method",
                sectionId: "algorithm-higher-order"
              }
            ]
          },
          {
            name: "函数不可导",
            type: "framework",
            sectionId: "algorithm-nondifferentiable",
            children: [
              {
                name: "元启发式算法",
                type: "method",
                sectionId: "algorithm-metaheuristics"
              },
              {
                name: "模型驱动的黑箱优化",
                type: "method",
                sectionId: "algorithm-blackbox"
              }
            ]
          },
          {
            name: "组合策略",
            type: "framework",
            sectionId: "algorithm-combined",
            children: [
              {
                name: "最优性条件与对偶理论",
                type: "method",
                sectionId: "algorithm-optimality"
              },
              {
                name: "约束处理策略",
                type: "method",
                sectionId: "algorithm-constraints"
              },
              {
                name: "分解与分布式方法",
                type: "method",
                sectionId: "algorithm-decomposition"
              },
              {
                name: "全局确定性方法",
                type: "method",
                sectionId: "algorithm-global"
              }
            ]
          }
        ]
      },
      {
        name: "8. 按规模与结构可利用性",
        type: "category",
        sectionId: "scale-structure-utility",
        children: [
          {
            name: "稀疏性",
            type: "method",
            sectionId: "scale-sparsity"
          },
          {
            name: "几何流形结构",
            type: "method",
            sectionId: "scale-manifold"
          },
          {
            name: "数据驱动的求和结构",
            type: "method",
            sectionId: "scale-data-driven"
          },
          {
            name: "并行与分布式计算",
            type: "method",
            sectionId: "scale-parallel"
          }
        ]
      }
    ]
  };

  // 完整的Markdown内容
  const fullMarkdownContent = `
## 1.3 数学优化方法的分类

对数学优化问题进行系统性分类，通常遵循两条逻辑主线：首先，从多个维度剖析问题的内在特征（定义其**是什么**）；其次，根据这些特征匹配相应的求解策略与算法范式（决定其**怎么解**）。根据这两条逻辑主线，数学优化问题的分类方法如下：

### 1.3.1 按决策变量类型（What Is Being chosen?） {#decision-variable-type}

决策变量的取值空间是优化问题的最基本分类维度，它从根本上决定了问题的数学特性和求解范式。

#### 1.3.1.1 连续优化 (Continuous Optimization) {#decision-variable-type-continuous}

 - 决策变量在**连续的实数域**上取值（例如，$x \\in \\mathbb{R}^n$）。求解的核心在于分析和利用目标与约束函数的光滑性（可导性）、凸性以及数值稳定性（条件数）。

 - *示例*: 非线性最小二乘、凸优化、光束法平差 (Bundle Adjustment) 中的相机位姿与三维点坐标。

#### 1.3.1.2 离散/整数优化 (Discrete / Integer Optimization) {#decision-variable-type-discrete}

- 决策变量的取值范围是**离散的**，例如整数集（$x \\in \\mathbb{Z}^n$）或一个有限的符号集合。由于解空间具有**组合爆炸**的特性，这类问题通常是 NP-hard 的，其求解依赖于组合搜索（如分支定界）或连续松弛等技巧。

- *示例*: 影像镶嵌中的最佳接缝线搜索、语义分割中为每个像素分配类别标签。

#### 1.3.1.3 混合整数优化 (Mixed-Integer Optimization, MIP/MICP) {#decision-variable-type-mixed}

- 问题中同时包含**连续变量**和**离散/整数变量**，是工业应用中最常见也最具挑战性的问题之一。通常采用**分支定界法 (Branch and Bound)**，并结合对整数变量的**连续松弛 (Continuous Relaxation)** 来进行求解。
- *示例*: 在确定测站是否建立（0-1决策）的同时，优化已建测站的具体坐标（连续决策）。

#### 1.3.1.4 类别/排列/集合优化 (Categorical / Permutation / Set Optimization) {#decision-variable-type-combinatorial}

- 决策变量本身是**非数值的组合对象**，如一个类别的选择、一个任务的排列顺序、或一个项目子集的选取。这类问题在建模阶段，通常通过引入**指示变量（如 0-1 变量）**，将其**等价地转化为整数规划或图论问题**（如旅行商问题、指派问题）来求解。
- *示例*: 多个控制点的最佳观测顺序规划（旅行商问题）、特征点集合的最优匹配（图匹配问题）。

#### 1.3.1.5 小结

> **NOTE**：
>
> - 连续变量便于使用导数信息；离散变量更依赖图搜索、分支定界或元启发式。
> - 变量类型的差异导致了求解范式的根本不同：**连续优化**的核心是利用**梯度**等导数信息在光滑的空间中进行迭代搜索；而**离散优化**的本质是在组合结构中进行**系统性的搜索、剪枝与枚举**。
> - 变量是类别、排列或集合；这类问题本质上具有**组合特性**，因此常通过引入二进制变量等方式，将其**转化为整数规划或图优化问题**（如图匹配、旅行商问题等）来建模求解。

### 1.3.2 按约束与可行域结构（Where Can We choose?） {#constraint-feasible-region}

约束条件定义了决策变量的合法取值范围，即**可行域 (Feasible Region)**。这个区域的几何结构是决定优化问题求解难度的核心因素，也是区分不同算法策略的根本依据。

#### 1.3.2.1 无约束优化（Unconstrained Optimization） {#constraint-unconstrained}

- **定义**: 决策变量的选取不受任何等式或不等式限制，可以在其完整的定义域（如 $\\mathbb{R}^n$）内自由移动。

- **核心**: 仅优化目标函数，不涉及等式或不等式约束；优化的焦点完全集中在目标函数自身的分析性质上，例如其光滑性（梯度）、曲率（Hessian 矩阵）、[强凸性](https://zh.wikipedia.org/wiki/%E5%87%B8%E5%87%BD%E6%95%B0)、[Lipschitz 常数](https://zh.wikipedia.org/zh-cn/%E5%88%A9%E6%99%AE%E5%B8%8C%E8%8C%A8%E9%80%A3%E7%BA%8C)等性质。

- *示例*:
  - 无任何先验限制的最小二乘曲线拟合。
  - 使用 L-BFGS 等算法优化深度学习模型中的网络权重。

#### 1.3.2.2 有约束优化（Constrained Optimization） {#constraint-constrained}

- **定义**: 存在等式、不等式或边界条件，这些条件共同“雕刻”出可行域。算法的每一步迭代都必须确保解的有效性，即始终停留在可行域内或向其逼近。

- **核心分水岭**: 可行域的**凸性**是该领域最重要的分界线，它直接将有约束优化问题划分为性质截然不同的两大类。
  - **凸可行域**（Convex Feasible Region）
    - **特点**: 可行域是一个**凸集**（集合内任意两点的连线段仍在该集合内）。当目标函数也是凸函数时，问题便构成了**凸优化**，其最显著的优点是：**任何局部最优解都必定是全局最优解**。

    - **求解范式**: 这种优良性质使得投影法、内点法、对偶方法等一系列高效算法能够被应用，从而可靠地找到全局最优解。

    - *示例*:
      - 测绘数据配准中，要求变换参数必须使所有控制点落在某个预定义的**凸包**内。
      - 地物优化选取时，要求选取目标的中心点必须位于一个**凸多边形**边界内。

  - **非凸可行域**（Non-Convex Feasible Region）
    - **特点**: 可行域是**非凸**的，其几何形状可能包含“凹陷”、孔洞，甚至由多个不相连的子区域构成。这导致优化过程极易陷入**局部最优解**——即在小邻域内最优，但并非全局最佳。

    - **求解范式**: 求解这类问题极具挑战性，通常依赖于复杂的全局优化算法（如分支定界）、启发式策略（如模拟退火），或者对可行域进行**凸松弛 (Convex Relaxation)** 来寻找一个近似解。

    - *示例*:
      - 影像镶嵌中，搜索一条避开特定障碍物区域（非凸区域）的最佳路径。
      - 在复杂三维城市模型中，因地形或建筑遮挡（导致非凸可行域）而进行的无人机飞行路径优化。

#### 1.3.2.3 小结 {#constraint-summary}


> **NOTE**：可行域的几何结构是算法选择的**核心路标**。一个**凸**的可行域通常意味着问题是**易解的**（Tractable），存在通向全局最优的可靠路径；而一个**非凸**的可行域则往往意味着问题是**难解的**（Intractable），求解时必须在**解的质量**与**计算效率**之间做出权衡。


### 1.3.3 按目标与模型结构（What Does the Cost Look like?） {#objective-model-structure}

目标函数与约束的数学形式是决定优化问题难度和求解方法的核心。该分类下的最重要分界线是**凸性（Convexity）**，它直接关系到我们能否保证找到全局最优解。

#### 1.3.3.1 通用框架：非线性规划 (NLP) {#objective-nlp}

非线性规划 (Nonlinear Programming) 是最广泛的优化问题模型，涵盖了所有目标函数或约束中至少存在一个非线性项的场景。

- **公式**：$ \\min\\{ f(x) \\;|\\; g_i(x) \\le 0,\\ h_j(x) = 0 \\}$
- **特点**: 目标或约束中至少有一个是非线性的；可分为凸与非凸两类，求解方法多样（梯度法、牛顿法、SQP 等）。
- **核心属性**: NLP 的核心分野在于**凸性**。
  - **凸优化**: 若目标函数 $f(x)$ 与不等式约束函数 $g_i(x)$ 均为凸函数，且等式约束 $h_j(x)$ 均为仿射函数，则该问题为凸优化问题。其关键优势是**任何局部最优解即为全局最优解**。
  - **非凸优化**: 不满足以上条件的问题。求解通常只能保证收敛到局部最优，寻找全局解极具挑战性。
- *示例*: 复杂地形条件下的无人机能耗最优航迹规划，其能耗模型与地形约束通常是非线性的。

#### 1.3.3.2 核心类别：凸优化及其特例 {#objective-convex}

凸优化问题因其优良的理论性质和高效的求解算法，在工程与科研中扮演着至关重要的角色。以下是其最重要的子类，按结构从简单到复杂排列。

##### 1.3.3.2.1 线性规划（Linear Programming, LP） {#objective-lp}
  - **公式**：$ \\max\\{ c^\\top x \\;|\\; x \\in \\mathbb{R}^n, \\; A x \\le b, \\; x \\ge 0 \\}$
  - **特点：**目标函数和所有约束条件均为**线性**表达式。这是理论最成熟、求解效率最高的优化问题类型。
  - **求解方法**: 单纯形法 (Simplex Method)、内点法 (Interior-Point Methods)。
  - *示例*: 已知多名内页作业员，每人完成标准图幅的工作量、所需时间和人工成本已知。目标是在满足总作业面积、交付时间等约束条件的前提下，合理分配任务给不同的作业员，使**总人工成本最小**。

##### 1.3.3.2.2 二次规划（Quadratic Programming, QP） {#objective-qp}
  - **公式**：$ \\min\\{ \\tfrac12 x^\\top Q x + c^\\top x \\;|\\; x \\in \\mathbb{R}^n, \\; A x \\le b, \\; x \\ge 0 \\}$
  - **特点：**约束条件为线性，但目标函数是**二次**的。当目标函数的二次型矩阵 $Q$ 为半正定（$Q \\succeq 0$） 时，该 QP 问题是凸的。
  - *示例*: GNSS 单历元定位的**加权最小二乘**问题，其本质就是一个无约束的凸 QP 问题。

##### 1.3.3.2.3 二次约束二次规划（Quadratically Constrained Quadratic Programming, QCQP） {#objective-qcqp}
  - **公式**：$ \\min\\{ \\tfrac12 x^\\top Q_0 x + c_0^\\top x \\;|\\; x \\in \\mathbb{R}^n, \\; x^\\top Q_i x + d_i^\\top x + e_i \\le 0,\\ i=1,\\dots,m \\}$
  - **特点：**目标函数和不等式约束函数均为二次形式。当所有相关的二次型矩阵均为半正定时，问题是凸的。
  - *示例*: 在地理围栏（由多个圆形或椭球区域定义）约束下的最优基站位置选择。

##### 1.3.3.2.4 锥规划（Conic Programming: SOCP / SDP / ExpCone 等） {#objective-conic}
  - **公式（统一标准形）**：$ \\min\\{ c^\\top x \\;|\\; A x + s = b,\\; s \\in \\mathcal{K} \\}$ ，其中 $\\mathcal{K}$ 为标准锥（如二阶锥、半正定锥、指数锥）。
  - **特点：**椎规划是LP, QP, QCQP 的一个强大推广和**统一框架**。它将可行域定义在一个凸锥上，能够优雅地处理更多类型的约束。
  - **重要子类**：
    - **二阶锥规划 (SOCP)**: 当 $\\mathcal{K}$ 是二阶锥（Lorentz cone）时，可以建模形如 $ \\|A_i x + b_i\\|_2 \\le c_i^\\top x + d_i $ 的约束，如 GNSS 椭球误差约束定位问题；
    - **半定规划 (SDP)**: 当 $\\mathcal{K}$ 是半正定矩阵锥时，变量是矩阵且被约束为半正定。建模为 $ \\min\\{ \\langle C, X \\rangle \\;|\\; \\langle A_j, X \\rangle = b_j,\\ X \\succeq 0 \\}$，如影像匹配的图匹配 SDP 松弛。

##### 1.3.3.2.5 几何规划（Geometric Programming, GP） {#objective-gp}
  - **公式**（正变量 $x>0$）：$ \\min\\{ f_0(x) \\;|\\; f_i(x) \\le 1,\\ g_j(x) = 1 \\}$ ，其中 $f_i$ 为 正项式，$g_j$ 为 单项式；经对数变量变换可转为凸优化问题。

  - **特点**: 变量为正，函数由**正项式 (Posynomial)** 和**单项式 (Monomial)** 构成。虽然其原始形式非凸，但可通过对数变换**转化为一个标准的凸优化问题**。

  - *示例*: 基于功率与信噪比模型（通常是乘积与比例关系）的最优传感器布设。

#### 1.3.3.3 非凸优化及其挑战 {#objective-nonconvex}

非凸优化问题是指目标函数或约束函数中至少存在一个非凸函数的优化问题。这类问题的主要特征是可能存在多个局部最优解，且局部最优解不一定是全局最优解。

- **特点**: 问题的可行域或目标函数为非凸，导致优化过程容易陷入局部最优解
- **求解挑战**: 需要专门的全局优化算法，如分支定界、遗传算法、模拟退火等
- *示例*: 复杂地形下的多无人机协同路径规划，涉及避障约束和多目标优化

#### 1.3.3.4 具有特殊结构的非线性问题 {#objective-special}

##### 1.3.3.4.1 非线性最小二乘（Nonlinear Least Squares, NLLS） {#objective-nlls}
  - **公式**：$ \\min\\{ \\tfrac12 \\sum_{i=1}^{N} \\| W_i^{1/2} r_i(x) \\|_2^2 \\}$
  - **特点**: 目标函数是**一系列非线性残差项的平方和**。这是测量数据处理领域最常见的问题形式。
  - **求解方法**: 高斯-牛顿法 (Gauss-Newton)、列文伯格-马夸尔特法 (LM)。这些算法利用了问题的一阶导数（雅可比矩阵）信息来高效地近似二阶（Hessian）信息。
  - *示例*: **光束法平差 (Bundle Adjustment, BA)**、点云迭代最近点配准 (ICP)。

##### 1.3.3.4.2 复合 / 非光滑优化（Composite / Nonsmooth Optimization） {#objective-composite}
  - **公式**：$ \\min\\{ g(x) + h(x) \\}$，其中 $g$ 光滑（且 $\\nabla g$ 具 Lipschitz 连续），$h$ 为非光滑凸正则或指示函数。
  - **特点：**目标函数可拆分为一个**光滑项**和一个**非光滑但结构简单**的项（如 $L_1$ 范数）。这类模型在引入稀疏性、低秩等正则化先验时非常有效。
  - **求解方法**: 近端梯度法 (ISTA, FISTA)、交替方向乘子法 (ADMM)。

#### 1.3.3.5 小结

> **NOTE**：“锥规划”是凸优化的重要统一框架，LP、SOCP、SDP 都是它的特例，尤其适合处理几何、矩阵与不等式混合约束。

### 1.3.4 按不确定性与信息结构（What Do We know?） {#uncertainty-information}

在现实世界的决策问题中，模型参数往往并非完全已知。我们对不确定性信息的掌握程度，决定了优化建模的哲学和方法。

#### 1.3.4.1 确定性优化 (Deterministic) {#uncertainty-deterministic}

- **核心假设**: 模型中的所有参数（如成本、资源、物理系数）都被假定为**精确已知且固定不变**的数值。
- **建模范式**: 这是最经典的优化框架，我们直接基于这些已知的、确定的数据构建模型并求解。它为分析问题提供了一个理想化的基准。

#### 1.3.4.2 随机优化（Stochastic） {#uncertainty-stochastic}

- **核心假设**: 参数的不确定性可以通过一个**已知的概率分布**来精确刻画（例如，某个测量误差服从高斯分布）。我们虽然不知道下一次观测的具体值，但了解其统计规律。
- **建模范式**: 我们的目标不再是针对某一个特定场景优化，而是追求**期望性能的最优** ($\\min_x \\mathbb{E}[f(x, \\xi)]$) 或保证决策在**大概率下是可行**的（机会约束：$\\Pr\\{g(x, \\xi) \\le 0\\} \\ge 1 - \\alpha$）。它本质上是一种"押注于大概率事件"的策略。
- **特点**: 利用统计分布信息优化平均性能或满足概率性需求。在随机规划中，参数服从已知分布的假设成立时，可以基于期望做决策优化。
- *示例*: 调度车辆时，假设行驶时间服从某个已知的交通流量分布，目标是最小化平均总耗时。

#### 1.3.4.3 鲁棒优化（Robust） {#uncertainty-robust}

- **核心假设**: 我们对参数的概率分布信息掌握很少，甚至一无所知，唯一可知的是它所属的一个**有界的不确定集** $\\mathcal{U}$。（例如，传感器误差的范围在 $[-1, 1]$ 内，但具体如何分布未知）。
- **建模范式**: 这是一种**风险规避**的、偏向保守的策略。其目标是制定一个能够**抵御一切可能的最坏情况**的决策，即求解 min-max 问题：$\\min_x \\max_{\\xi \\in \\mathcal{U}} f(x, \\xi)$ 解的性能可能不是平均最好的，但它提供了一个**绝对的安全保证**。
- **特点**: 无需精确分布，只需参数可能范围，即可保证解对最坏情形的稳健性。鲁棒方法强调最差表现保障，与随机方法不同。
- *示例*: 设计桥梁时，必须确保其结构能够承受在所有可能的最极端风力、载荷组合（不确定集）下的最大应力。

#### 1.3.4.4 分布鲁棒优化（Distributionally Robust Optimization, DRO） {#uncertainty-dro}  

- **核心假设**: 它是随机优化与鲁棒优化的精妙结合。我们既不知道确切的概率分布，但又不仅仅只知道一个简单的边界。我们掌握了部分统计信息（如均值、方差），从而可以将真实的、未知的分布限定在一个 **"分布族"**（一个由许多可能分布组成的集合）之内。
- **建模范式**: 目标是在这个**最坏的可能分布**下，使期望性能达到最优。它在利用已有统计信息的同时，也考虑了分布本身的误差，从而寻求一种**数据驱动的稳健性**。 
- **特点**: 兼顾分布信息与最坏期望性能，提升模型可靠性。

#### 1.3.4.5 小结

> **NOTE:**
>
> - **随机优化**是一种"乐观"的平均主义：相信基于已知的概率分布进行决策，长期来看是最佳的。
> - **鲁棒优化**是一种"悲观"的绝对主义：不相信任何概率信息，只为最坏的情况做准备，追求绝对安全。
> - **分布鲁棒优化**是一种"现实"的折衷主义：承认我们拥有的概率信息可能不完美，并在此基础上寻求最稳健的决策。

### 1.3.5 按时域与决策流程（When Do We choose ?） {#time-decision-process}

决策是在一个时间点上一次性完成，还是随着时间的推移和信息的更新而逐步进行，这构成了优化问题的另一重要维度。

#### 1.3.5.1 静态优化（Static Optimization） {#time-static}

- **定义**: 所有决策变量在一个**单一、整体**的优化过程中被同时确定。这类问题不考虑时间的演化，假设所有必需信息在决策之初就已完全掌握。
- *示例*: 在一次性的成本预算和精度要求下，为一片区域规划**最佳的地面控制点布局**，这是一个典型的静态资源配置问题。

#### 1.3.5.2 动态优化（Dynamic Optimization） {#time-dynamic}

- **定义**: 描述一个系统在连续或离散的时间阶段中，其状态 $x_t$ 如何根据当前状态和施加的控制 $u_t$ 演化到下一状态 $x_{t+1} = f(x_t, u_t, t)$。目标是寻找最优的控制序列 $\\{u_0, u_1, \\ldots, u_{T-1}\\}$，以最小化总代价。其模型可以表示为：

    $$\\min_{u_{0:T-1}} \\sum_{t=0}^{T-1} \\ell(x_t, u_t, t) + \\phi(x_T), \\quad \\text{s.t. } x_{t+1} = f(x_t, u_t, t)$$

- **最优控制**是研究此类问题的数学分支，尤其在连续时间系统上，它提供了如**庞特里亚金最大值原理**或**HJB方程**等强大的理论分析工具。

#### 1.3.5.3 动态规划（Dynamic Programming, DP） {#time-dp}

- **定义**: 这并非一类问题，而是一种强大的**求解方法论**，专门用于解决具有**最优子结构**性质的多阶段决策问题。其核心思想（贝尔曼最优性原理）是将一个复杂的全局问题，**递归地分解**为一系列更简单的、嵌套的子问题来求解。
- **典型模型**: **马尔可夫决策过程(MDP)** 是动态规划在随机环境下的经典应用模型，常通过价值迭代、策略迭代等DP算法求解最优策略。

#### 1.3.5.4 在线/流式优化（Online/Streaming Optimization） {#time-online}

- **定义**: 决策者必须在信息**逐步到达**的过程中，**顺序地、不可撤销地**做出决策，且在任一时刻都无法预知未来的信息。
- **评估标准**: 其性能优劣通常不与某个固定的最优值比较，而是通过 **"遗憾"(Regret)** 来衡量——即在线算法的总代价与一个"事后诸葛亮"（能看到所有未来信息的离线最优算法）的总代价之间的差距。目标是最小化累积遗憾。

#### 1.3.5.5 探索型序贯优化（Exploration-based/Bayesian-type Optimization） {#time-bayesian}

- **定义**: 适用于目标函数本身是 **"黑箱"**（无解析形式、不可导）且 **评估代价高昂** 的场景。决策过程是一个 **"边做边学"** 的序列，每一步都需要在两个目标间权衡：
  - **探索(Explore)**: 在不确定的区域进行尝试，以期发现更好的解。
  - **利用(Exploit)**: 在当前已知的最优区域附近进行挖掘，以优化现有最佳解。
- **典型范式**: **贝叶斯优化(Bayesian Optimization)**、**多臂老虎机(Multi-Armed Bandit)**。

#### 1.3.5.6 小结

> **NOTE**: 动态优化 vs. 动态规划
>
> - **动态优化**指一类随时间演化的问题建模范畴，着重于系统如何跨时间决策与状态演化。  
> - **动态规划**是解决这类问题（以及其他具有最优子结构问题）的一种核心**方法**，本质是用来解决具有阶段性决策结构的问题，并不等同于动态优化本身。  

### 1.3.6 按目标个数与解的概念（What Is optimal?） {#objective-count-solution}

"最优"的定义并非一成不变。它取决于我们是在追求单一维度的极致，还是在多个冲突的目标间寻求平衡。同时，根据问题的复杂性和求解能力，我们对解的"最优性"也有着不同层次的定义。

#### 1.3.6.1 单目标优化（Single-Objective） {#objective-single}

- **定义**: 整个优化过程致力于最小化或最大化一个**单一的、明确的标量性能指标**（如成本、误差、效率）。目标是找到一个或一组使该指标达到极致的决策变量。
- **解的形态**: 寻求一个在整个可行域上最优的**点**。
- *示例*: 测绘中最小化总测量误差，或最大化控制网的精度。

#### 1.3.6.2 多目标优化（Multi-Objective/Vector Optimization） {#objective-multi}

- **定义**: 同时优化两个或更多**相互冲突**的目标函数（例如，成本vs.精度，时效vs.覆盖范围）。由于目标间的冲突，不存在能使所有目标同时达到最优的单一"完美解"。
- **解的形态**: 优化的结果不是一个点，而是一个**解的集合**，被称为 **帕累托最优集(Pareto Optimal Set)**。此集合中的任何一个解都无法在不牺牲其他目标性能的前提下，改进任何一个目标。这些解在目标函数空间中构成的边界被称为 **帕累托前沿(Pareto Front)**。
- *示例*: 无人机路径规划中同时优化飞行时间、能耗和覆盖区域面积。

#### 1.3.6.3 解的质量层级 {#objective-quality}

对于一个给定的优化问题，我们找到的解可能处于以下不同的质量层级：

##### 1.3.6.3.1 全局最优解(Global Optimum) {#objective-global}
  - **定义**: 在**整个可行域**内，使得目标函数值达到最优的解。这是优化理论追求的终极目标，但对于非凸问题，寻找并验证全局最优解通常极其困难。

##### 1.3.6.3.2 局部最优解(Local Optimum) {#objective-local}
  - **定义**: 在其邻近的一个小区域内，该解对应的目标函数值优于所有其他相邻解。绝大多数通用的非线性优化算法，其收敛性保证都止步于局部最优。

##### 1.3.6.3.3 近似最优解(Approximate/Near-Optimal Solution) {#objective-approximate}
  - **定义**: 一个可行解，其目标函数值被证明在全局最优值的某个可接受的误差范围（$\\epsilon$）之内。在处理NP-难问题或需要快速获得高质量解的场景中，这是一个非常实用的目标。

##### 1.3.6.3.4 工程可用解(Pragmatic/Engineering Solution) {#objective-engineering}
  - **定义**: 一个在实践中被接受的解，它可能并非严格的数学最优，但在**计算成本、实现复杂度、鲁棒性和模型可解释性**等多个工程维度上达到了最佳的综合平衡。

#### 1.3.6.4 小结

> **NOTE**: 从单目标到多目标的转变是一种核心的**思维范式转换**。优化的目的不再是给出一个唯一的"答案"，而是**描绘出所有最优权衡的全貌（即帕累托前沿）**，从而将最终的决策权交给设计者，让他们可以根据外部的、更高层次的偏好，在这个"最优菜单"中进行选择。

### 1.3.7 按求解策略与算法范式（How To solve?） {#solving-strategy-algorithm}

一旦问题被数学化地定义，我们就需要选择合适的算法"引擎"来求解。算法范式根据其利用的信息（如导数）、处理问题的策略以及适用范围而异。

#### 1.3.7.1 目标函数光滑可微 {#algorithm-differentiable}

对于目标函数和约束光滑可微的场景，利用导数信息是最高效的途径。算法的效率和成本与其利用导数的阶数密切相关。

##### 1.3.7.1.1 一阶方法(First-Order Methods) {#algorithm-first-order}
- **核心思想**: 仅利用目标函数的**一阶导数（梯度）** 信息来确定下降方向。这类方法每次迭代的计算成本极低，内存占用小，特别适合于**大规模**和**超大规模**问题。
- **代表算法**: 梯度下降法(Gradient Descent)、投影梯度法、近端梯度法(ISTA, FISTA)、随机梯度下降(SGD)及其变体(Adam, RMSProp)。
##### 1.3.7.1.2 二阶与拟二阶方法(Second-Order & Quasi-Newton Methods) {#algorithm-second-order}
- **核心思想**: 同时利用**一阶导数（梯度）** 和**二阶导数（Hessian矩阵）** 信息。Hessian矩阵描述了函数的局部曲率，使得算法能够更精准地走向最优解，从而具有更快的收敛速度（如二次收敛）。
- **分类**:
    - **牛顿法(Newton's Method)**: 直接计算并使用真实的Hessian矩阵。收敛快，但Hessian矩阵的计算和求逆成本极高，限制了其在大规模问题中的应用。
    - **拟牛顿法(Quasi-Newton Methods)**: 通过梯度在迭代过程中的变化，来**近似** Hessian矩阵或其逆。它们在收敛速度和计算成本之间取得了绝佳的平衡，是中等规模优化问题的**主力算法**。代表有BFGS, L-BFGS。
##### 1.3.7.1.3 利用特殊结构的算法(Structure-Exploiting Methods) {#algorithm-structure}
- **核心思想**: 针对**非线性最小二乘(NLLS)** 这类具有"残差平方和"特殊结构的问题，可以通过其**一阶雅可比矩阵**，构造出对Hessian矩阵的有效近似。
- **代表算法**: 高斯-牛顿法(Gauss-Newton)和列文伯格-马夸尔特法(LM)，它们比标准的拟牛顿法更高效地利用了问题结构，是求解BA、ICP等问题的黄金标准。
##### 1.3.7.1.4 高阶优化方法(Higher-Order Methods) {#algorithm-higher-order}
- **核心思想**: 利用三阶或更高阶的导数信息来构造迭代，以期获得比二阶方法更快的收敛阶数（如三阶收敛）。
- **典型方法**:
    - **Halley类方法（第三阶）**: 在某些条件下迭代收敛速度可达三阶，计算量大于牛顿法但提升显著。
    - **高级d阶Newton方法**: 以d阶Taylor展开并通过Sum-of-Squares+半定规划求解每步更新，具有局部d阶收敛性能。
- **局限性**: 高阶导数张量的计算、存储和处理会带来**指数级的计算复杂度**，导致其在实践中，尤其是在测绘等高维应用领域，几乎**不被使用**，主要停留在理论研究和特定的低维高精度计算中。

#### 1.3.7.2 函数不可导 {#algorithm-nondifferentiable}

当函数的导数不可计算、不存在，或函数评估成本极高时，我们需要不依赖梯度的优化方法。

##### 1.3.7.2.1 元启发式算法(Metaheuristics) {#algorithm-metaheuristics}
- **核心思想**: 受自然界（如进化、退火、鸟群觅食）或社会现象启发的随机搜索算法。它们通过**全局搜索**与**局部探索**的平衡，致力于在复杂解空间中找到高质量的解。
- **特点**: 适用性广，不要求函数有任何良好数学性质。但其**不保证收敛到全局最优**，且结果通常具有随机性。
- **代表算法**: 遗传算法(GA)、粒子群优化(PSO)、模拟退火(SA)。
##### 1.3.7.2.2 模型驱动的黑箱优化(Model-Based Black-Box Optimization) {#algorithm-blackbox}
- **核心思想**: 针对评估成本极高的"黑箱"函数，通过少量采样点构建一个**代理模型(Surrogate Model)** 来近似原函数，然后在廉价的代理模型上进行优化，以智能地决定下一个评估点。
- **代表算法**: 贝叶斯优化(Bayesian Optimization)、CMA-ES。

#### 1.3.7.3 组合策略 {#algorithm-combined}

以下是更高层次的方法论，它们通常与上述具体算法相结合，用于处理约束、分解问题或分析最优性。

##### 1.3.7.3.1 最优性条件与对偶理论(Optimality Conditions & Duality) {#algorithm-optimality}
- **定义**: 这不是求解算法，而是**分析和验证解的理论基石**。**KKT（Karush–Kuhn–Tucker）条件** 为有约束优化问题提供了最优解所必须满足的充要条件。**拉格朗日对偶** 则通过将原问题转化为一个对偶问题，为分析问题、获得最优值的下界以及设计算法（如ADMM）提供了强大工具。
##### 1.3.7.3.2 约束处理策略(Constraint Handling Strategies) {#algorithm-constraints}
- **核心思想**: 将复杂的**有约束问题**转化为一系列更易于求解的**无约束（或简单约束）问题**。
- **主要方法**: 惩罚法(Penalty Methods)、障碍法(Barrier Methods, 内点法的基础)、增广拉格朗日法(Augmented Lagrangian Methods)。
##### 1.3.7.3.3 分解与分布式方法(Decomposition & Distributed Methods) {#algorithm-decomposition}
- **核心思想**: 将一个耦合的、大规模的全局问题，**分解**为多个可以**并行或独立求解**的、规模更小的子问题，并通过一个协调机制来整合子问题的解。
- **代表算法**: 交替方向乘子法(ADMM)、丹茨格-沃尔夫分解(Dantzig-Wolfe Decomposition)。
##### 1.3.7.3.4 全局确定性方法(Deterministic Global Optimization) {#algorithm-global}
- **核心思想**: 针对非凸问题，通过系统性的空间划分和剪枝（如区间算法、分支定界法），在有限时间内**保证找到全局最优解**并提供其最优性证明。
- **局限性**: 计算成本随问题维度指数增长，通常只适用于小到中等规模的问题。

#### 1.3.7.4 小结

> **NOTE**:
>
> - **一阶vs.二阶**: 本质上是**扩展性(Scalability)** 与**收敛速度(Convergence Speed)** 之间的权衡。
> - **梯度vs.黑箱**: 是利用**问题结构**的高效率与**模型普适性**之间的权衡。
> - **启发式vs.确定性**: 是在复杂问题上快速获得**高质量解**与在特定问题上追求**理论最优保证**之间的权衡。

### 1.3.8 按规模与结构可利用性（How Big and structured?） {#scale-structure-utility}

在现代应用中，优化问题不仅类型各异，其规模和内部结构更是千差万别。高效的求解方案往往不是选择一个通用算法，而是深度利用问题特有的结构，并匹配相应的计算范式。

#### 1.3.8.1 稀疏性 (Sparsity) {#scale-sparsity}

- **背景**: 在许多大规模问题中，绝大多数变量之间没有直接的相互作用。这种关系体现在问题的关键矩阵（如雅可比矩阵、Hessian 矩阵）上，即矩阵中绝大部分元素为零。
- **策略**: 利用稀疏性是**大规模优化的第一准则**。通过使用稀疏矩阵存储格式和专门的代数库，可以避免无效的零元素存储和计算。更重要的是，它使得像**共轭梯度法 (CG)** 及其预条件版本 (PCG) 等迭代求解器变得极为高效，因为这些方法的核心仅依赖于（稀疏）矩阵与向量的乘法操作。
- *示例*: 在**光束法平差 (Bundle Adjustment, BA)** 中，一个三维点仅与观测到它的少数几个相机位姿相关，这使得整个问题的雅可比矩阵和高斯-牛顿近似的 Hessian 矩阵呈现出鲜明的**块稀疏**结构。利用此结构是实现大规模 BA 的关键。

#### 1.3.8.2 几何流形结构 (Geometric Manifold Structure) {#scale-manifold}

- **背景**: 当决策变量天然地存在于一个**非欧几里得的弯曲空间**（即流形）上时，传统的优化方法可能会失效。例如，旋转矩阵必须保持其正交性，不能简单地当作普通向量来处理。
- **策略**: **黎曼优化 (Riemannian Optimization)** 将经典的优化算法（如梯度下降、信赖域法）从欧氏空间推广到了流形上。它通过在流形的“切空间”上进行计算，并利用“收缩 (Retraction)”操作将更新后的点拉回到流形上，从而在整个优化过程中严格保持变量的几何约束。
- *示例*: 在机器人学或 SLAM 的姿态估计中，需要在旋转矩阵构成的特殊正交群 **SO(3)** 流形上进行优化，以确保得到的姿态是物理有效的。

#### 1.3.8.3 数据驱动的求和结构 (Data-Driven Summation Structure) {#scale-data-driven}

- **背景**: 在机器学习和现代数据科学中，目标函数常常是一个在海量数据集上**所有样本损失之和**的形式：$F\\left( x \\right) = \\sum\\limits_{i = 1}^N {{f_i}\\left( x \\right)} $，其中 $N$ 可能达到数百万甚至数十亿。

- **策略**: **随机与小批量方法 (Stochastic / Mini-batch Methods)** 是应对此结构的核心武器。其思想是，在每次迭代中，不计算完整的、昂贵的真实梯度（需要遍历所有 $N$ 个样本），而是随机抽取一小部分样本（一个 mini-batch）来计算一个**梯度的无偏估计**。虽然每一步的更新方向带有噪声，但其极低的计算成本使得算法可以极快地进行大量迭代，从而在宏观上实现高效收敛。

- **代表算法**: **随机梯度下降 (SGD)** 及其各种先进的变体 (Adam, AdaGrad, RMSProp)，以及方差缩减方法 (SVRG, SAGA)。

#### 1.3.8.4 并行与分布式计算 (Parallel & Distributed Computing) {#scale-parallel}

- **背景**: 当单台计算机的计算能力或内存已无法承载问题的规模时，利用多核、GPU 或计算机集群进行协同计算成为必然选择。
- **策略**: 这是实现大规模优化的**计算范式**。它可以是：
  - **数据并行**: 将海量数据分布到不同节点上，各节点并行计算其上的梯度分量，最后汇总更新（常见于深度学习训练）。
  - **模型并行**: 将巨大的模型（如参数矩阵）本身切分到不同节点上。
  - **算法分解**: 采用像 **ADMM** 这样的算法，将一个大的耦合问题，在数学上分解为多个可以并行求解、再通过消息传递来协调的子问题。
- *示例*: 在一个多 GPU 服务器上，并行处理不同批次的遥感影像数据，以加速一个大规模深度学习分类模型的训练。

#### 1.3.8.5 小结 {#scale-summary}

> **NOTE:**
>
> 解决前沿的大规模问题，往往需要将上述策略**协同设计**：例如，利用 **ADMM** 将一个大规模 **BA 问题**分解到**分布式集群**上，每个节点内部再利用**稀疏线性代数**和 **LM 算法**进行高效求解。这体现了**问题结构、优化算法、计算架构**三者的深度融合。

`;

  // 滚动定位到指定章节并高亮标题
  const scrollToSection = (sectionId) => {
    const scrollContainer = markdownRef.current;
    const element = scrollContainer?.querySelector(`#${sectionId}`);
    
    if (element && scrollContainer) {
      // 获取目标元素相对于滚动容器的位置
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const targetScrollTop = scrollTop + elementRect.top - containerRect.top - 20; // 20px顶部间距
      
      // 手动实现平滑滚动
      const startScrollTop = scrollContainer.scrollTop;
      const distance = targetScrollTop - startScrollTop;
      const duration = 800; // 800ms动画时长
      let startTime = null;
      
      const smoothScroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        
        // 使用easeInOutCubic缓动函数
        const ease = progress < 0.5 
          ? 4 * progress * progress * progress 
          : (progress - 1) * (2 * progress - 2) * (2 * progress - 2) + 1;
        
        scrollContainer.scrollTop = startScrollTop + distance * ease;
        
        if (progress < 1) {
          requestAnimationFrame(smoothScroll);
        }
      };
      
      requestAnimationFrame(smoothScroll);
      
      // 高亮处理
      setTimeout(() => {
        // 清除所有标题的高亮
        scrollContainer.querySelectorAll('h2, h3').forEach(h => {
          h.style.backgroundColor = 'transparent';
          h.style.padding = '0';
          h.style.borderRadius = '';
        });
        
        // 高亮当前标题
        element.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
        element.style.padding = '0.5rem';
        element.style.borderRadius = '0.5rem';
      }, 100); // 延迟一点执行高亮，确保滚动开始
      
      setHighlightedSection(sectionId);
      
      // 3秒后移除高亮
      setTimeout(() => {
        element.style.backgroundColor = 'transparent';
        element.style.padding = '0';
      }, 3000);
    }
  };

  // 创建思维导图
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const container = svgRef.current.parentElement;
    const width = container.clientWidth || 700;
    const height = container.clientHeight || 450;
    const margin = { top: 50, right: 50, bottom: 50, left: 50 };

    svg.attr("width", width).attr("height", height);

    // 创建力导向布局 - 统一布局，但聚焦模式下调整参数
    const simulation = d3.forceSimulation()
      .force("link", d3.forceLink().id(d => d.id)
        .distance(d => {
          // 基于缩放级别调整距离 - 更激进的缩放算法
          const baseDistance = 120;
          const focusDistance = 80;
          // 使用更强的缩放函数：缩小时距离显著减小，放大时距离适度增加
          const distanceScale = zoomLevel > 1 
            ? 1 / Math.pow(zoomLevel, 0.3) // 放大时距离适度减小
            : 1 / Math.pow(zoomLevel, 0.8); // 缩小时距离显著减小
          
          if (viewMode === 'focus' && focusedNode) {
            // 聚焦模式下调整连线距离，突出层级关系
            const sourceNode = nodes.find(n => n.id === d.source.id);
            const targetNode = nodes.find(n => n.id === d.target.id);
            const sourceRel = sourceNode?.relationship || 'distant';
            const targetRel = targetNode?.relationship || 'distant';
            
            // 焦点节点与其父子节点的距离更近
            if ((sourceRel === 'focus' && ['parent', 'child'].includes(targetRel)) ||
                (targetRel === 'focus' && ['parent', 'child'].includes(sourceRel))) {
              return focusDistance * distanceScale; // 更近的距离突出层级关系
            }
            return baseDistance * distanceScale; // 默认距离
          }
          return baseDistance * distanceScale; // 正常模式默认距离
        }))
      .force("charge", d3.forceManyBody()
        .strength(d => {
          // 基于缩放级别调整排斥力 - 与距离缩放保持一致
          const chargeScale = zoomLevel > 1 
            ? 1 / Math.pow(zoomLevel, 0.4) // 放大时排斥力适度减小
            : 1 / Math.pow(zoomLevel, 1.2); // 缩小时排斥力显著减小
          
          if (viewMode === 'focus' && focusedNode) {
            // 聚焦模式下调整排斥力，关键节点排斥力更强
            switch (d.relationship) {
              case 'focus': return -400 * chargeScale;     // 焦点节点强排斥，保持中心位置
              case 'parent':
              case 'child': return -200 * chargeScale;     // 父子节点中等排斥
              default: return -50 * chargeScale;           // 其他节点弱排斥，不干扰主体
            }
          }
          return -300 * chargeScale; // 正常模式统一排斥力
        }))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide()
        .radius(d => {
          const baseRadius = d.scale ? (d.name.length * 6 + 20) * d.scale : 50;
          return baseRadius + 5;
        }));

    // 聚焦模式下添加额外的中心吸引力和约束，确保关键节点可见
    if (viewMode === 'focus' && focusedNode) {
      simulation.force("centerAttraction", d3.forceRadial()
        .radius(d => {
          switch (d.relationship) {
            case 'focus': return 0;        // 焦点节点固定在中心
            case 'parent': return 90;      // 父节点围绕中心
            case 'child': return 120;      // 子节点围绕中心  
            default: return 250;           // 其他节点推向外围
          }
        })
        .strength(d => {
          switch (d.relationship) {
            case 'focus': return 0.9;      // 焦点节点强力居中
            case 'parent': 
            case 'child': return 0.5;      // 父子节点较强吸引力
            default: return 0.08;          // 其他节点很弱吸引力
          }
        })
        .x(width / 2)
        .y(height / 2));
        
      // 添加额外的边界力，防止关键节点被推出视图
      simulation.force("boundary", () => {
        nodes.forEach(d => {
          if (['focus', 'parent', 'child'].includes(d.relationship)) {
            const nodeRadius = 60;
            const padding = 30;
            const maxX = width - nodeRadius - padding;
            const maxY = height - nodeRadius - padding;
            const minX = nodeRadius + padding;
            const minY = nodeRadius + padding;
            
            // 如果节点接近边界，施加向内的力
            if (d.x < minX) d.vx += (minX - d.x) * 0.1;
            if (d.x > maxX) d.vx += (maxX - d.x) * 0.1;
            if (d.y < minY) d.vy += (minY - d.y) * 0.1;
            if (d.y > maxY) d.vy += (maxY - d.y) * 0.1;
          }
        });
      });
    }

    // 转换数据为图结构
    const nodes = [];
    const links = [];
    let nodeId = 0;

    const processNode = (node, parentId = null, level = 0) => {
      const currentId = nodeId++;
      
      // 根据视图模式决定节点显示策略
      let shouldShow = visibleNodeTypes[node.type];
      let relationship = 'unrelated';
      
      if (viewMode === 'focus' && focusedNode) {
        relationship = getNodeRelationship(node, focusedNode);
        // 在聚焦模式下，只高亮显示焦点节点及其直接父子节点
        // 所有节点都显示，但只有focus、parent、child高亮，其他都是低透明度背景
        // shouldShow保持为true，让所有节点都显示，通过opacity控制视觉效果
        
        // 调试信息 - 只在开发环境显示
      }
      // overview模式显示所有可见类型的节点
      
      if (shouldShow) {
        nodes.push({
          id: currentId,
          name: node.name,
          type: node.type,
          case: node.case,
          anchor: node.anchor,
          sectionId: node.sectionId,
          level: level,
          expanded: isExpanded,
          relationship: relationship,
          opacity: getNodeOpacity(node, level, relationship),
          scale: getNodeScale(node, level, relationship),
        });

        if (parentId !== null) {
          links.push({
            source: parentId,
            target: currentId
          });
        }
      }

      if (node.children && shouldShow) {
        node.children.forEach(child => {
          processNode(child, currentId, level + 1);
        });
      }

      return currentId;
    };

    // 获取节点的直接关联关系
    const getNodeRelationship = (node, focusNode) => {
      if (!focusNode) return 'unrelated';
      
      if (node.sectionId === focusNode.sectionId) {
        return 'focus'; // 焦点节点本身
      }
      
      // 寻找父子关系
      const isDirectParent = findDirectParent(focusNode, node);
      const isDirectChild = findDirectChild(focusNode, node);
      const siblings = findSiblings(focusNode);
      const isSibling = siblings.some(sibling => sibling.sectionId === node.sectionId);
      
      
      if (isDirectParent) return 'parent';
      if (isDirectChild) return 'child';
      if (isSibling) return 'sibling';
      
      return 'distant';
    };

    // 查找节点完整信息的辅助函数
    const findNodeInData = (targetSectionId, rootNode = mindmapData) => {
      if (rootNode.sectionId === targetSectionId) {
        return rootNode;
      }
      if (rootNode.children) {
        for (const child of rootNode.children) {
          const result = findNodeInData(targetSectionId, child);
          if (result) return result;
        }
      }
      return null;
    };

    // 查找直接父节点
    const findDirectParent = (targetNode, candidateParent) => {
      const searchForParent = (node) => {
        if (node.children) {
          // 检查是否是直接子节点
          for (const child of node.children) {
            if (child.sectionId === targetNode.sectionId) {
              const isParent = node.sectionId === candidateParent.sectionId;
              return isParent;
            }
          }
          // 递归搜索
          for (const child of node.children) {
            const result = searchForParent(child);
            if (result !== null) return result;
          }
        }
        return null;
      };
      return searchForParent(mindmapData) || false;
    };

    // 查找直接子节点  
    const findDirectChild = (parentNode, candidateChild) => {
      const parentData = findNodeInData(parentNode.sectionId);
      if (parentData && parentData.children) {
        const isChild = parentData.children.some(child => child.sectionId === candidateChild.sectionId);
        return isChild;
      }
      return false;
    };

    // 查找兄弟节点
    const findSiblings = (targetNode) => {
      const findSiblingsInNode = (node) => {
        if (node.children) {
          // 检查子节点中是否包含目标节点
          const targetChild = node.children.find(child => child.sectionId === targetNode.sectionId);
          if (targetChild) {
            // 返回除目标节点外的所有兄弟节点
            const siblings = node.children.filter(sibling => sibling.sectionId !== targetNode.sectionId);
            return siblings;
          }
          // 递归搜索
          for (const child of node.children) {
            const result = findSiblingsInNode(child);
            if (result.length > 0) return result;
          }
        }
        return [];
      };
      return findSiblingsInNode(mindmapData);
    };

    // 判断节点是否在当前路径中
    const isNodeInPath = (node) => {
      return currentPath.includes(node.name);
    };

    // 获取节点透明度 - 基于关联关系
    const getNodeOpacity = (node, level, relationship) => {
      if (viewMode === 'focus') {
        switch (relationship) {
          case 'focus': return 1.0;           // 焦点节点完全不透明
          case 'parent': return 0.95;         // 直接父节点高透明度
          case 'child': return 0.95;          // 直接子节点高透明度  
          case 'sibling': return 0.2;         // 兄弟节点中等透明度
          case 'distant': return 0.15;        // 远距离节点低透明度
          default: return 0.1;                // 其他节点低透明度
        }
      }
      return Math.max(0.6, 1 - level * 0.1);
    };

    // 获取节点缩放比例 - 突出焦点节点
    const getNodeScale = (node, level, relationship) => {
      const baseScale = zoomLevel;
      
      if (viewMode === 'focus') {
        switch (relationship) {
          case 'focus': return baseScale * 1.8;    // 焦点节点显著放大
          case 'parent': return baseScale * 1.3;   // 父节点适度放大
          case 'child': return baseScale * 1.3;    // 子节点适度放大
          default: return baseScale * 0.8;         // 其他节点缩小
        }
      }
      
      return baseScale * Math.max(0.8, 1 - level * 0.05);
    };

    // 获取聚焦模式下的固定位置
    const getFocusPosition = (node, relationship, relatedNodes) => {
      if (viewMode !== 'focus' || !focusedNode) return null;
      
      const centerX = width / 2;
      const centerY = height / 2;
      
      if (relationship === 'focus') {
        // 焦点节点固定在中心
        return { x: centerX, y: centerY, fixed: true };
      }
      
      if (relationship === 'parent') {
        // 父节点固定在焦点节点上方
        return { x: centerX, y: centerY - 120, fixed: true };
      }
      
      if (relationship === 'child') {
        // 获取所有子节点
        const childNodes = relatedNodes.filter(n => n.relationship === 'child');
        const childIndex = childNodes.findIndex(c => c.sectionId === node.sectionId);
        const childCount = childNodes.length;
        
        if (childCount === 1) {
          return { x: centerX, y: centerY + 120, fixed: true };
        } else if (childCount === 2) {
          return { 
            x: centerX + (childIndex === 0 ? -100 : 100), 
            y: centerY + 120, 
            fixed: true 
          };
        } else {
          // 多个子节点呈扇形分布在下方
          const angle = (Math.PI / 2) * (childIndex - (childCount - 1) / 2) / Math.max(1, childCount - 1);
          const radius = 120;
          return {
            x: centerX + Math.sin(angle) * radius,
            y: centerY + Math.cos(angle) * radius,
            fixed: true
          };
        }
      }
      
      // 兄弟节点不需要特殊位置，让它们和其他背景节点一样随机分布
      
      // 其他节点不固定位置，但设置为远离中心的随机位置
      return {
        x: Math.random() * width * 0.3 + (Math.random() > 0.5 ? width * 0.7 : 0),
        y: Math.random() * height * 0.3 + (Math.random() > 0.5 ? height * 0.7 : 0),
        fixed: false
      };
    };

    // 获取节点层级（简化版本）
    const getNodeLevel = (node) => {
      const levelMap = { root: 0, category: 1, framework: 2, subframework: 3, method: 4 };
      return levelMap[node.type] || 0;
    };

    processNode(mindmapData);

    // 应急处理：如果聚焦模式下没有显示任何节点，显示焦点节点及其路径上的节点
    if (viewMode === 'focus' && focusedNode && nodes.length === 0) {
      // 重新处理，这次显示路径上的节点
      nodes.length = 0;
      links.length = 0;
      nodeId = 0;
      
      const processNodeFallback = (node, parentId = null, level = 0) => {
        const currentId = nodeId++;
        const isInCurrentPath = currentPath.includes(node.name);
        const shouldShow = visibleNodeTypes[node.type] && (isInCurrentPath || level <= 2);
        
        if (shouldShow) {
          nodes.push({
            id: currentId,
            name: node.name,
            type: node.type,
            sectionId: node.sectionId,
            level: level,
            relationship: node.sectionId === focusedNode.sectionId ? 'focus' : 'context',
            opacity: node.sectionId === focusedNode.sectionId ? 1.0 : 0.7,
            scale: node.sectionId === focusedNode.sectionId ? zoomLevel * 2.0 : zoomLevel,
            position: { weight: 1, distance: 120, centerAttraction: 1 }
          });

          if (parentId !== null) {
            links.push({ source: parentId, target: currentId });
          }
        }

        if (node.children && (shouldShow || isInCurrentPath)) {
          node.children.forEach(child => {
            processNodeFallback(child, shouldShow ? currentId : parentId, level + 1);
          });
        }

        return currentId;
      };
      
      processNodeFallback(mindmapData);
    }


    // 添加SVG滤镜定义
    if (viewMode === 'focus' && focusedNode) {
      const defs = svg.select("defs").empty() ? svg.append("defs") : svg.select("defs");
      
      // 呼吸发光效果滤镜
      const glowFilter = defs.selectAll("#focus-line-glow").empty() ? 
        defs.append("filter").attr("id", "focus-line-glow") : 
        defs.select("#focus-line-glow");
        
      glowFilter
        .attr("x", "-50%")
        .attr("y", "-50%")
        .attr("width", "200%")
        .attr("height", "200%");

      // 清除之前的内容
      glowFilter.selectAll("*").remove();
      
      // 创建发光效果
      glowFilter.append("feGaussianBlur")
        .attr("stdDeviation", "2")
        .attr("result", "coloredBlur");
      
      const feMerge = glowFilter.append("feMerge");
      feMerge.append("feMergeNode").attr("in", "coloredBlur");
      feMerge.append("feMergeNode").attr("in", "SourceGraphic");
    }

    // 创建连接线 - 根据视图模式调整样式
    const link = svg.append("g")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", d => {
        if (viewMode === 'focus' && focusedNode) {
          // 获取连线两端的节点信息
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const targetId = typeof d.target === 'object' ? d.target.id : d.target;
          const sourceNode = nodes.find(n => n.id === sourceId);
          const targetNode = nodes.find(n => n.id === targetId);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 关键节点间的连线使用更亮的颜色
          if (['focus', 'parent', 'child'].includes(sourceRel) && 
              ['focus', 'parent', 'child'].includes(targetRel)) {
            return "#60a5fa"; // 亮蓝色，比默认更亮但不刺眼
          }
          return "#6b7280"; // 背景连线稍微弱化
        }
        return "#4a5568"; // 默认颜色
      })
      .attr("stroke-width", d => {
        if (viewMode === 'focus' && focusedNode) {
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const targetId = typeof d.target === 'object' ? d.target.id : d.target;
          const sourceNode = nodes.find(n => n.id === sourceId);
          const targetNode = nodes.find(n => n.id === targetId);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 关键节点间的连线保持默认粗细
          if (['focus', 'parent', 'child'].includes(sourceRel) && 
              ['focus', 'parent', 'child'].includes(targetRel)) {
            return 2; // 与默认一致的粗细
          }
          return 1.5; // 背景连线稍细一些
        }
        return 2; // 默认粗细
      })
      .attr("opacity", d => {
        if (viewMode === 'focus' && focusedNode) {
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const targetId = typeof d.target === 'object' ? d.target.id : d.target;
          const sourceNode = nodes.find(n => n.id === sourceId);
          const targetNode = nodes.find(n => n.id === targetId);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 关键节点间的连线保持默认透明度
          if (['focus', 'parent', 'child'].includes(sourceRel) && 
              ['focus', 'parent', 'child'].includes(targetRel)) {
            return 0.6; // 与默认一致的透明度
          }
          return 0.3; // 背景连线适度弱化但仍可见
        }
        return 0.6; // 默认透明度
      })
      .attr("filter", d => {
        if (viewMode === 'focus' && focusedNode) {
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const targetId = typeof d.target === 'object' ? d.target.id : d.target;
          const sourceNode = nodes.find(n => n.id === sourceId);
          const targetNode = nodes.find(n => n.id === targetId);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 关键节点间的连线添加发光效果
          if (['focus', 'parent', 'child'].includes(sourceRel) && 
              ['focus', 'parent', 'child'].includes(targetRel)) {
            return "url(#focus-line-glow)";
          }
        }
        return null;
      })
      .attr("class", d => {
        if (viewMode === 'focus' && focusedNode) {
          const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
          const targetId = typeof d.target === 'object' ? d.target.id : d.target;
          const sourceNode = nodes.find(n => n.id === sourceId);
          const targetNode = nodes.find(n => n.id === targetId);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 关键节点间的连线添加呼吸动画类
          if (['focus', 'parent', 'child'].includes(sourceRel) && 
              ['focus', 'parent', 'child'].includes(targetRel)) {
            return "focus-link-breathing";
          }
        }
        return "";
      });

    // 创建节点组
    const nodeSelection = svg.append("g")
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .attr("cursor", "pointer")
      .call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended));

    // 根据节点类型设置样式
    nodeSelection.each(function(d) {
      const g = d3.select(this);
      
      // 节点背景，根节点增加内边距
      const rect = g.append("rect")
        .attr("width", d => {
          const padding = d.type === "root" ? 40 : 20; // 根节点加大内边距
          return (d.name.length * 12 + padding) * d.scale;
        })
        .attr("height", d => {
          const height = d.type === "root" ? 45 : 35; // 根节点高度也稍微增加
          return height * d.scale;
        })
        .attr("x", d => {
          const padding = d.type === "root" ? 40 : 20;
          return -((d.name.length * 6 + padding/2) * d.scale);
        })
        .attr("y", d => {
          const height = d.type === "root" ? 45 : 35;
          return -(height/2 * d.scale);
        })
        .attr("rx", 8)
        .attr("fill", d => {
          switch(d.type) {
            case "root": return "#3b82f6";           // 蓝色
            case "category": return "#10b981";       // 绿色
            case "framework": return "#f59e0b";      // 橙色
            case "subframework": return "#8b5cf6";   // 紫色
            case "method": return "#ef4444";         // 橘红色
            default: return "#64748b";
          }
        })
        .attr("stroke", d => {
          if (d.relationship === 'focus') return "#fbbf24";        // 焦点节点金色边框
          if (d.relationship === 'parent') return "#10b981";       // 父节点绿色边框
          if (d.relationship === 'child') return "#3b82f6";        // 子节点蓝色边框
          if (d.relationship === 'sibling') return "#8b5cf6";      // 兄弟节点紫色边框
          return "#ffffff";                                        // 其他节点白色边框
        })
        .attr("stroke-width", d => {
          if (d.relationship === 'focus') return 4;    // 焦点节点粗边框
          if (['parent', 'child'].includes(d.relationship)) return 2.5; // 直接关联节点中粗边框
          return 1.5;                                   // 其他节点正常边框
        })
        .attr("opacity", d => d.opacity);

      // 节点文本
      g.append("text")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .attr("font-size", d => {
          const baseSize = d.type === "root" ? 14 : 12;
          return `${baseSize * d.scale}px`;
        })
        .attr("font-weight", d => d.type === "root" ? "bold" : "normal")
        .attr("fill", "white")
        .attr("opacity", d => d.opacity)
        .text(d.name);

      // 添加悬停效果和交互
      g.on("mouseenter", function(event, d) {
        setSelectedNode(d);
        // 悬停时适度放大，但不影响鱼眼布局
        if (d.relationship !== 'focus') { // 焦点节点已经是最大，不需要再放大
          d3.select(this).select("rect")
            .transition()
            .duration(150)
            .attr("stroke-width", (d.relationship === 'focus' ? 4 : 
                                   ['parent', 'child'].includes(d.relationship) ? 2.5 : 1.5) + 1);
        }
      })
      .on("mouseleave", function(event, d) {
        // 恢复原始边框
        d3.select(this).select("rect")
          .transition()
          .duration(150)
          .attr("stroke-width", d.relationship === 'focus' ? 4 : 
                               ['parent', 'child'].includes(d.relationship) ? 2.5 : 1.5);
      })
      .on("click", function(event, d) {
        // 设置为焦点节点 - 确保包含完整的节点信息
        const fullNodeData = {
          ...d,
          name: d.name,
          sectionId: d.sectionId,
          type: d.type
        };
        
        
        setFocusedNode(fullNodeData);
        
        // 切换到聚焦视图
        setViewMode('focus');
        
        // 更新当前路径
        const pathToNode = getPathToNode(d);
        setCurrentPath(pathToNode);
        
        // 滚动到对应章节
        if (d.sectionId) {
          scrollToSection(d.sectionId);
        }
      });
    });

    // 拖拽功能
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // 清除所有固定位置约束，让物理模拟自然工作
    nodes.forEach(d => {
      d.fx = null;
      d.fy = null;
    });

    // 启动仿真
    simulation
      .nodes(nodes)
      .on("tick", () => {
        // 在聚焦模式下，确保关键节点始终在视图内
        if (viewMode === 'focus' && focusedNode) {
          nodes.forEach(d => {
            if (['focus', 'parent', 'child'].includes(d.relationship)) {
              // 为关键节点设置边界约束，确保它们始终可见
              const nodeRadius = 50; // 节点半径的估算值
              const padding = 20; // 边界内边距
              
              d.x = Math.max(nodeRadius + padding, Math.min(width - nodeRadius - padding, d.x));
              d.y = Math.max(nodeRadius + padding, Math.min(height - nodeRadius - padding, d.y));
            }
          });
        }
        
        link
          .attr("x1", d => d.source.x)
          .attr("y1", d => d.source.y)
          .attr("x2", d => d.target.x)
          .attr("y2", d => d.target.y);

        nodeSelection
          .attr("transform", d => `translate(${d.x},${d.y})`);
      });

    // 只有在有link force的时候才设置links
    const linkForce = simulation.force("link");
    if (linkForce) {
      linkForce.links(links);
      
      // 完全重新设置距离函数以获取最新的zoomLevel值
      const newDistanceFunction = (d) => {
        // 基于缩放级别调整距离 - 更激进的缩放算法
        const baseDistance = 120;
        const focusDistance = 80;
        // 正确的缩放逻辑，但加强缩放效果使其更明显
        const distanceScale = Math.pow(zoomLevel, 1.5); // 加强缩放效果，使距离变化更明显
        
        const finalDistance = baseDistance * distanceScale;
        
        if (viewMode === 'focus' && focusedNode) {
          // 聚焦模式下调整连线距离，突出层级关系
          const sourceNode = nodes.find(n => n.id === d.source.id);
          const targetNode = nodes.find(n => n.id === d.target.id);
          const sourceRel = sourceNode?.relationship || 'distant';
          const targetRel = targetNode?.relationship || 'distant';
          
          // 焦点节点与其父子节点的距离更近
          if ((sourceRel === 'focus' && ['parent', 'child'].includes(targetRel)) ||
              (targetRel === 'focus' && ['parent', 'child'].includes(sourceRel))) {
            return focusDistance * distanceScale; // 更近的距离突出层级关系
          }
          return finalDistance; // 默认距离
        }
        // 全局视图模式下，所有连线都应用缩放
        return finalDistance;
      };
      
      linkForce.distance(newDistanceFunction);
    }

    // 同样更新排斥力的缩放
    const chargeForce = simulation.force("charge");
    if (chargeForce) {
      chargeForce.strength(d => {
        // 基于缩放级别调整排斥力 - 与距离缩放保持一致
        const chargeScale = zoomLevel > 1 
          ? 1 / Math.pow(zoomLevel, 0.4) // 放大时排斥力适度减小
          : 1 / Math.pow(zoomLevel, 1.2); // 缩小时排斥力显著减小
        
        if (viewMode === 'focus' && focusedNode) {
          // 聚焦模式下调整排斥力，关键节点排斥力更强
          switch (d.relationship) {
            case 'focus': return -400 * chargeScale;     // 焦点节点强排斥，保持中心位置
            case 'parent':
            case 'child': return -200 * chargeScale;     // 父子节点中等排斥
            default: return -50 * chargeScale;           // 其他节点弱排斥，不干扰主体
          }
        }
        return -300 * chargeScale; // 正常模式统一排斥力
      });
    }
    
    // 强制更新所有连线和力的初始化值
    simulation.nodes(nodes);
    if (linkForce) {
      linkForce.links(links);
    }

    // 更激烈的重启动方式确保缩放生效
    simulation.stop();
    simulation.alpha(1).alphaTarget(0.05).restart();
    
    // 延时停止目标alpha，让仿真自然冷却
    setTimeout(() => {
      simulation.alphaTarget(0);
    }, 3000);

  }, [viewMode, focusedNode, visibleNodeTypes, isExpanded, zoomLevel]);

  return (
    <>
      <section
        id={id}
        className="snap-section relative overflow-hidden"
        style={{ backgroundColor: 'var(--bg-deep)' }}
      >
        {/* 思维导图信息素传递动画背景 */}
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 1, opacity: 0.5 }}>
          {/* 神经网络节点群 - 均匀分布的20个节点 */}
          <div className="absolute inset-0">
            {Array.from({ length: 23 }).map((_, i) => {
              // 四大经典星座组合：天蝎座、猎户座、大熊座、仙女座
              const positions = [
                // 天蝎座 - 左上区域（6颗星）
                { x: 15, y: 20, tier: 'primary', size: 'large', constellation: 'scorpio' },  // 心宿二（天蝎座主星）
                { x: 22, y: 15, tier: 'bright', size: 'medium', constellation: 'scorpio' }, // 头部
                { x: 28, y: 25, tier: 'bright', size: 'medium', constellation: 'scorpio' }, // 身体
                { x: 32, y: 35, tier: 'bright', size: 'small', constellation: 'scorpio' },  // 尾部开始
                { x: 35, y: 45, tier: 'secondary', size: 'small', constellation: 'scorpio' }, // 尾部中段
                { x: 38, y: 55, tier: 'secondary', size: 'tiny', constellation: 'scorpio' }, // 尾部末端
                
                // 猎户座 - 右上区域（7颗星）
                { x: 75, y: 15, tier: 'bright', size: 'medium', constellation: 'orion' }, // 参宿四（左肩）
                { x: 85, y: 18, tier: 'bright', size: 'medium', constellation: 'orion' }, // 参宿七（右肩）
                { x: 72, y: 35, tier: 'primary', size: 'large', constellation: 'orion' }, // 参宿一（腰带中心）
                { x: 68, y: 33, tier: 'bright', size: 'small', constellation: 'orion' },  // 参宿二（腰带左）
                { x: 76, y: 37, tier: 'bright', size: 'small', constellation: 'orion' },  // 参宿三（腰带右）
                { x: 74, y: 50, tier: 'secondary', size: 'medium', constellation: 'orion' }, // 参宿六（左脚）
                { x: 82, y: 52, tier: 'secondary', size: 'medium', constellation: 'orion' }, // 参宿五（右脚）
                
                // 大熊座（北斗七星）- 左下区域（7颗星）
                { x: 12, y: 65, tier: 'bright', size: 'medium', constellation: 'ursa' }, // 天枢
                { x: 18, y: 62, tier: 'bright', size: 'medium', constellation: 'ursa' }, // 天璇
                { x: 24, y: 68, tier: 'bright', size: 'medium', constellation: 'ursa' }, // 天玑
                { x: 30, y: 70, tier: 'bright', size: 'medium', constellation: 'ursa' }, // 天权
                { x: 36, y: 75, tier: 'primary', size: 'large', constellation: 'ursa' }, // 玉衡（斗柄中心）
                { x: 42, y: 80, tier: 'bright', size: 'medium', constellation: 'ursa' }, // 开阳
                { x: 48, y: 85, tier: 'secondary', size: 'small', constellation: 'ursa' }, // 摇光
                
                // 仙女座 - 右下区域（3颗星）
                { x: 88, y: 75, tier: 'primary', size: 'large', constellation: 'andromeda' }, // 仙女座α（壁宿二）
                { x: 82, y: 82, tier: 'bright', size: 'medium', constellation: 'andromeda' }, // 仙女座β
                { x: 75, y: 88, tier: 'secondary', size: 'medium', constellation: 'andromeda' } // 仙女座γ
              ];
              const pos = positions[i];
              return (
                <div
                  key={`neuron-${i}`}
                  className="absolute"
                  style={{
                    left: `${pos.x}%`,
                    top: `${pos.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {/* 星座节点核心 - 自然亮度分层 */}
                  <div
                    className="relative rounded-full"
                    style={{
                      width: `${pos.size === 'large' ? 16 : pos.size === 'medium' ? 12 : pos.size === 'small' ? 8 : 4}px`,
                      height: `${pos.size === 'large' ? 16 : pos.size === 'medium' ? 12 : pos.size === 'small' ? 8 : 4}px`,
                      background: pos.tier === 'primary' 
                        ? 'radial-gradient(circle, #fff 0%, #fbbf24 30%, #f59e0b 70%, rgba(245, 158, 11, 0.8) 100%)'
                        : pos.tier === 'bright'
                        ? 'radial-gradient(circle, #fef3c7 0%, #fbbf24 60%, rgba(245, 158, 11, 0.9) 100%)'
                        : pos.tier === 'secondary'
                        ? 'radial-gradient(circle, #fde68a 0%, #fbbf24 70%, rgba(251, 191, 36, 0.7) 100%)'
                        : 'radial-gradient(circle, #fbbf24 20%, rgba(251, 191, 36, 0.5) 80%, transparent 100%)',
                      animation: `starPulse${pos.tier === 'primary' ? 0 : pos.tier === 'bright' ? 1 : pos.tier === 'secondary' ? 2 : 3} ${
                        pos.tier === 'primary' ? 2.5 : 
                        pos.tier === 'bright' ? 3.5 + i * 0.15 : 
                        pos.tier === 'secondary' ? 4.5 + i * 0.2 : 6 + i * 0.3
                      }s infinite ease-in-out`,
                      filter: pos.tier === 'primary' ? 'blur(0.2px)' : 'blur(0.3px)',
                      boxShadow: pos.tier === 'primary' 
                        ? '0 0 25px rgba(251, 191, 36, 0.9), inset 0 0 6px rgba(255, 255, 255, 0.5)'
                        : pos.tier === 'bright'
                        ? '0 0 15px rgba(251, 191, 36, 0.7)'
                        : pos.tier === 'secondary'
                        ? '0 0 8px rgba(251, 191, 36, 0.5)'
                        : '0 0 4px rgba(251, 191, 36, 0.3)'
                    }}
                  />
                  
                  {/* 星光散射效果 */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: pos.tier === 'primary'
                        ? 'radial-gradient(circle, transparent 30%, rgba(255, 255, 255, 0.3) 50%, rgba(251, 191, 36, 0.2) 70%, transparent 100%)'
                        : pos.tier === 'bright'
                        ? 'radial-gradient(circle, transparent 40%, rgba(251, 191, 36, 0.2) 65%, transparent 100%)'
                        : 'radial-gradient(circle, transparent 50%, rgba(251, 191, 36, 0.1) 75%, transparent 100%)',
                      animation: `starGlow ${pos.tier === 'primary' ? 5 : 7 + i * 0.1}s infinite ease-in-out reverse`,
                      transform: pos.tier === 'primary' ? 'scale(3)' : pos.tier === 'bright' ? 'scale(2.2)' : 'scale(1.8)'
                    }}
                  />
                  
                  {/* 主星特殊闪烁 */}
                  {pos.tier === 'primary' && (
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, transparent 40%)',
                        animation: `primarySparkle 3s infinite ease-in-out`,
                        transform: 'scale(0.4)'
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* 动态连接线网络 - 分层无交叉设计 */}
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="connectionGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.1" />
                <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.1" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* 四大星座连接网络 - 按天文学准确连接 */}
            {[
              // 天蝎座连接（蝎子形状）
              { x1: 15, y1: 20, x2: 22, y2: 15, type: 'constellation', group: 'scorpio' }, // 心宿二到头部
              { x1: 22, y1: 15, x2: 28, y2: 25, type: 'constellation', group: 'scorpio' }, // 头部到身体
              { x1: 15, y1: 20, x2: 28, y2: 25, type: 'constellation', group: 'scorpio' }, // 心宿二到身体（主线）
              { x1: 28, y1: 25, x2: 32, y2: 35, type: 'constellation', group: 'scorpio' }, // 身体到尾部
              { x1: 32, y1: 35, x2: 35, y2: 45, type: 'constellation', group: 'scorpio' }, // 尾部连接
              { x1: 35, y1: 45, x2: 38, y2: 55, type: 'constellation', group: 'scorpio' }, // 尾部末端
              
              // 猎户座连接（猎人形状）
              { x1: 75, y1: 15, x2: 85, y2: 18, type: 'constellation', group: 'orion' }, // 双肩连线
              { x1: 75, y1: 15, x2: 68, y2: 33, type: 'constellation', group: 'orion' }, // 左肩到腰带
              { x1: 85, y1: 18, x2: 76, y2: 37, type: 'constellation', group: 'orion' }, // 右肩到腰带
              { x1: 68, y1: 33, x2: 72, y2: 35, type: 'constellation', group: 'orion' }, // 腰带三星连线
              { x1: 72, y1: 35, x2: 76, y2: 37, type: 'constellation', group: 'orion' }, // 腰带三星连线
              { x1: 72, y1: 35, x2: 74, y2: 50, type: 'constellation', group: 'orion' }, // 腰带到左脚
              { x1: 72, y1: 35, x2: 82, y2: 52, type: 'constellation', group: 'orion' }, // 腰带到右脚
              
              // 大熊座连接（北斗七星）
              { x1: 12, y1: 65, x2: 18, y2: 62, type: 'constellation', group: 'ursa' }, // 天枢-天璇
              { x1: 18, y1: 62, x2: 24, y2: 68, type: 'constellation', group: 'ursa' }, // 天璇-天玑
              { x1: 24, y1: 68, x2: 30, y2: 70, type: 'constellation', group: 'ursa' }, // 天玑-天权
              { x1: 30, y1: 70, x2: 36, y2: 75, type: 'constellation', group: 'ursa' }, // 天权-玉衡
              { x1: 36, y1: 75, x2: 42, y2: 80, type: 'constellation', group: 'ursa' }, // 玉衡-开阳
              { x1: 42, y1: 80, x2: 48, y2: 85, type: 'constellation', group: 'ursa' }, // 开阳-摇光
              { x1: 12, y1: 65, x2: 30, y2: 70, type: 'extend', group: 'ursa' }, // 斗口连线
              
              // 仙女座连接（三角形）
              { x1: 88, y1: 75, x2: 82, y2: 82, type: 'constellation', group: 'andromeda' }, // α-β连线
              { x1: 82, y1: 82, x2: 75, y2: 88, type: 'constellation', group: 'andromeda' }, // β-γ连线
              { x1: 88, y1: 75, x2: 75, y2: 88, type: 'extend', group: 'andromeda' }      // α-γ连线
            ].map((line, i) => (
              <line
                key={`connection-${i}`}
                x1={`${line.x1}%`} y1={`${line.y1}%`}
                x2={`${line.x2}%`} y2={`${line.y2}%`}
                stroke={line.type === 'primary' ? '#fbbf24' : 
                       line.type === 'constellation' ? 'rgba(251, 191, 36, 0.8)' :
                       line.type === 'extend' ? 'rgba(251, 191, 36, 0.6)' : 'rgba(251, 191, 36, 0.3)'}
                strokeWidth={line.type === 'primary' ? 1.5 : line.type === 'constellation' ? 1.2 : line.type === 'extend' ? 0.8 : 0.5}
                strokeDasharray={
                  line.type === 'primary' ? "none" : 
                  line.type === 'constellation' ? "none" : 
                  line.type === 'extend' ? "3,2" : "2,3"
                }
                strokeOpacity={line.type === 'primary' ? 0.9 : line.type === 'constellation' ? 0.7 : line.type === 'extend' ? 0.5 : 0.3}
                filter={line.type === 'primary' ? "url(#glow)" : undefined}
                strokeLinecap="round"
                style={{
                  animation: `constellationFlow${i % 4} ${
                    line.type === 'primary' ? 3 + i * 0.1 : 
                    line.type === 'constellation' ? 5 + i * 0.15 : 
                    line.type === 'extend' ? 7 + i * 0.2 : 9 + i * 0.25
                  }s infinite linear`
                }}
              />
            ))}
          </svg>
          
          {/* 信息素粒子群 - 基于新连接线的路径 */}
          <div className="absolute inset-0">
            {Array.from({ length: 24 }).map((_, i) => {
              // 四大星座信息素粒子路径
              const connections = [
                // 天蝎座粒子路径
                { start: { x: 15, y: 20 }, end: { x: 22, y: 15 }, intensity: 'high', group: 'scorpio' },
                { start: { x: 22, y: 15 }, end: { x: 28, y: 25 }, intensity: 'medium', group: 'scorpio' },
                { start: { x: 15, y: 20 }, end: { x: 28, y: 25 }, intensity: 'high', group: 'scorpio' },
                { start: { x: 28, y: 25 }, end: { x: 32, y: 35 }, intensity: 'medium', group: 'scorpio' },
                { start: { x: 32, y: 35 }, end: { x: 35, y: 45 }, intensity: 'low', group: 'scorpio' },
                { start: { x: 35, y: 45 }, end: { x: 38, y: 55 }, intensity: 'low', group: 'scorpio' },
                
                // 猎户座粒子路径
                { start: { x: 75, y: 15 }, end: { x: 85, y: 18 }, intensity: 'medium', group: 'orion' },
                { start: { x: 75, y: 15 }, end: { x: 68, y: 33 }, intensity: 'high', group: 'orion' },
                { start: { x: 85, y: 18 }, end: { x: 76, y: 37 }, intensity: 'high', group: 'orion' },
                { start: { x: 68, y: 33 }, end: { x: 72, y: 35 }, intensity: 'high', group: 'orion' },
                { start: { x: 72, y: 35 }, end: { x: 76, y: 37 }, intensity: 'high', group: 'orion' },
                { start: { x: 72, y: 35 }, end: { x: 74, y: 50 }, intensity: 'medium', group: 'orion' },
                { start: { x: 72, y: 35 }, end: { x: 82, y: 52 }, intensity: 'medium', group: 'orion' },
                
                // 大熊座粒子路径
                { start: { x: 12, y: 65 }, end: { x: 18, y: 62 }, intensity: 'medium', group: 'ursa' },
                { start: { x: 18, y: 62 }, end: { x: 24, y: 68 }, intensity: 'medium', group: 'ursa' },
                { start: { x: 24, y: 68 }, end: { x: 30, y: 70 }, intensity: 'medium', group: 'ursa' },
                { start: { x: 30, y: 70 }, end: { x: 36, y: 75 }, intensity: 'high', group: 'ursa' },
                { start: { x: 36, y: 75 }, end: { x: 42, y: 80 }, intensity: 'high', group: 'ursa' },
                { start: { x: 42, y: 80 }, end: { x: 48, y: 85 }, intensity: 'medium', group: 'ursa' },
                { start: { x: 12, y: 65 }, end: { x: 30, y: 70 }, intensity: 'low', group: 'ursa' },
                
                // 仙女座粒子路径
                { start: { x: 88, y: 75 }, end: { x: 82, y: 82 }, intensity: 'high', group: 'andromeda' },
                { start: { x: 82, y: 82 }, end: { x: 75, y: 88 }, intensity: 'medium', group: 'andromeda' },
                { start: { x: 88, y: 75 }, end: { x: 75, y: 88 }, intensity: 'low', group: 'andromeda' }
              ];
              
              const conn = connections[i % connections.length];
              const duration = conn.intensity === 'high' ? 3 + i * 0.1 : 
                             conn.intensity === 'medium' ? 5 + i * 0.2 : 7 + i * 0.3;
              const delay = i * 0.3;
              const size = conn.intensity === 'high' ? 2.5 : 
                          conn.intensity === 'medium' ? 2 : 1.5;
              const isReverse = i % 2 === 1;
              
              return (
                <div
                  key={`particle-${i}`}
                  className="absolute rounded-full"
                  style={{
                    width: `${size * 2}px`,
                    height: `${size * 2}px`,
                    background: conn.intensity === 'high' 
                      ? 'radial-gradient(circle, #fff 0%, #fbbf24 30%, #f59e0b 70%, transparent 100%)'
                      : conn.intensity === 'medium'
                      ? 'radial-gradient(circle, #fde68a 0%, #fbbf24 60%, transparent 100%)'
                      : 'radial-gradient(circle, #fbbf24 0%, rgba(251, 191, 36, 0.6) 80%, transparent 100%)',
                    transform: 'translate(-50%, -50%)',
                    opacity: 0,
                    filter: conn.intensity === 'high' ? 'blur(0.3px)' : 'blur(0.5px)',
                    boxShadow: conn.intensity === 'high' 
                      ? '0 0 8px #fbbf24, 0 0 15px rgba(251, 191, 36, 0.4)'
                      : `0 0 4px rgba(251, 191, 36, ${conn.intensity === 'medium' ? 0.6 : 0.3})`,
                    '--start-x': isReverse ? `${conn.end.x}%` : `${conn.start.x}%`,
                    '--start-y': isReverse ? `${conn.end.y}%` : `${conn.start.y}%`,
                    '--end-x': isReverse ? `${conn.start.x}%` : `${conn.end.x}%`,
                    '--end-y': isReverse ? `${conn.start.y}%` : `${conn.end.y}%`,
                    animation: `particlePath${i % 4} ${duration}s infinite ease-in-out ${delay}s`
                  }}
                />
              );
            })}
          </div>
          
          {/* 分散的微光粒子效果 */}
          <div className="absolute inset-0">
            {Array.from({ length: 35 }).map((_, i) => {
              const x = Math.random() * 100;
              const y = Math.random() * 100;
              const size = 1 + Math.random() * 2;
              const duration = 3 + Math.random() * 4;
              const delay = Math.random() * 8;
              
              return (
                <div
                  key={`ambient-${i}`}
                  className="absolute rounded-full"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    background: 'radial-gradient(circle, #fbbf24, transparent)',
                    transform: 'translate(-50%, -50%)',
                    animation: `ambientFloat ${duration}s infinite ease-in-out ${delay}s`,
                    opacity: Math.random() * 0.4 + 0.1
                  }}
                />
              );
            })}
          </div>
        </div>
        
        {/* 动画样式定义 */}
        <style>{`
          @keyframes starPulse0 {
            0%, 100% { transform: scale(0.95); opacity: 0.95; }
            50% { transform: scale(1.15); opacity: 1; }
          }
          @keyframes starPulse1 {
            0%, 100% { transform: scale(0.95); opacity: 0.8; }
            50% { transform: scale(1.1); opacity: 0.95; }
          }
          @keyframes starPulse2 {
            0%, 100% { transform: scale(0.98); opacity: 0.6; }
            50% { transform: scale(1.05); opacity: 0.85; }
          }
          @keyframes starPulse3 {
            0%, 100% { transform: scale(1.0); opacity: 0.4; }
            50% { transform: scale(1.05); opacity: 0.7; }
          }
          
          @keyframes primarySparkle {
            0%, 100% { 
              transform: scale(0.3); 
              opacity: 0; 
            }
            50% { 
              transform: scale(0.6); 
              opacity: 1; 
            }
          }
          
          @keyframes starGlow {
            0%, 100% { transform: scale(2.5); opacity: 0.05; }
            50% { transform: scale(3); opacity: 0.15; }
          }
          
          @keyframes neuronHalo0 {
            0%, 100% { transform: scale(2.5); opacity: 0.1; }
            50% { transform: scale(3.5); opacity: 0.3; }
          }
          @keyframes neuronHalo1 {
            0%, 100% { transform: scale(2.8); opacity: 0.15; }
            50% { transform: scale(3.2); opacity: 0.25; }
          }
          
          @keyframes constellationFlow0 {
            0% { stroke-dashoffset: 0; opacity: 0.5; }
            50% { opacity: 0.9; }
            100% { stroke-dashoffset: -20; opacity: 0.5; }
          }
          @keyframes constellationFlow1 {
            0% { stroke-dashoffset: -8; opacity: 0.4; }
            50% { opacity: 0.7; }
            100% { stroke-dashoffset: -28; opacity: 0.4; }
          }
          @keyframes constellationFlow2 {
            0% { stroke-dashoffset: -15; opacity: 0.3; }
            50% { opacity: 0.6; }
            100% { stroke-dashoffset: -35; opacity: 0.3; }
          }
          @keyframes constellationFlow3 {
            0% { stroke-dashoffset: -22; opacity: 0.25; }
            50% { opacity: 0.5; }
            100% { stroke-dashoffset: -42; opacity: 0.25; }
          }
          
          @keyframes particlePath0 {
            0% { 
              opacity: 0; 
              left: var(--start-x); 
              top: var(--start-y); 
              transform: translate(-50%, -50%) scale(0.8); 
            }
            15% { opacity: 1; }
            85% { opacity: 1; }
            100% { 
              opacity: 0; 
              left: var(--end-x); 
              top: var(--end-y); 
              transform: translate(-50%, -50%) scale(1.2); 
            }
          }
          @keyframes particlePath1 {
            0% { 
              opacity: 0; 
              left: var(--start-x); 
              top: var(--start-y); 
              transform: translate(-50%, -50%) scale(1); 
            }
            10% { opacity: 0.9; }
            90% { opacity: 0.9; }
            100% { 
              opacity: 0; 
              left: var(--end-x); 
              top: var(--end-y); 
              transform: translate(-50%, -50%) scale(0.9); 
            }
          }
          @keyframes particlePath2 {
            0% { 
              opacity: 0; 
              left: var(--start-x); 
              top: var(--start-y); 
              transform: translate(-50%, -50%) scale(0.9); 
            }
            20% { opacity: 0.7; }
            80% { opacity: 0.7; }
            100% { 
              opacity: 0; 
              left: var(--end-x); 
              top: var(--end-y); 
              transform: translate(-50%, -50%) scale(1.1); 
            }
          }
          @keyframes particlePath3 {
            0% { 
              opacity: 0; 
              left: var(--start-x); 
              top: var(--start-y); 
              transform: translate(-50%, -50%) scale(1.1); 
            }
            12% { opacity: 1; }
            88% { opacity: 1; }
            100% { 
              opacity: 0; 
              left: var(--end-x); 
              top: var(--end-y); 
              transform: translate(-50%, -50%) scale(0.7); 
            }
          }
          
          @keyframes ambientFloat {
            0%, 100% { 
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 0.1;
            }
            25% { 
              transform: translate(-50%, -50%) translateY(-10px) scale(1.2);
              opacity: 0.4;
            }
            50% { 
              transform: translate(-50%, -50%) translateX(5px) scale(1);
              opacity: 0.6;
            }
            75% { 
              transform: translate(-50%, -50%) translateY(8px) scale(1.1);
              opacity: 0.3;
            }
          }
        `}</style>

      <div className="relative z-10 w-full h-screen flex flex-col pt-[51px] pb-[96px]">

        {/* 主要内容区域 - 左右布局 */}
        <div className="flex-1 flex gap-6 px-8 pt-6 overflow-hidden justify-between" style={{ pointerEvents: 'auto' }}>
          {/* 左侧：知识图谱区域 */}
          <div className="flex flex-col" style={{ width: '45%', marginLeft: '5%' }}>
            {/* 搜索和控制区域 */}
            <div className="mb-4 space-y-3">
              {/* 智能搜索框 */}
              <div className="relative search-container">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleSearchFocus}
                  placeholder="🔍 搜索具体优化方法..."
                  className="w-full px-4 py-2 pr-2 rounded-lg border border-white/20 bg-black/20 backdrop-blur-sm text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:bg-black/30 transition-all relative z-30"
                  style={{ 
                    color: 'var(--ink-high)'
                  }}
                />
                
                {/* 按钮背景 - 低z-index，紧贴搜索框，明显的对比色 */}
                <div className="absolute right-0 top-0 bottom-0 bg-gray-700/80 backdrop-blur-sm rounded-r-lg z-10 border-l border-gray-600" style={{ width: '155px' }}></div>
                
                {/* 按钮容器 - 高z-index，确保可点击 */}
                <div className="absolute right-0 top-0 bottom-0 flex items-center px-1 gap-0.5 z-40">
                  <button
                    onClick={handleSearchSubmit}
                    className="px-2 py-1 rounded text-sm text-gray-300 hover:text-white hover:bg-teal-500/20 transition-all duration-200"
                    title="搜索"
                  >
                    🔍
                  </button>
                  <button
                    onClick={() => handleZoom('reset')}
                    className="px-2 py-1 rounded text-sm text-gray-300 hover:text-white hover:bg-blue-500/20 transition-all duration-200"
                    title="全局视图"
                  >
                    🌐
                  </button>
                  <button
                    onClick={() => handleZoom('in')}
                    className="px-2 py-1 rounded text-sm text-gray-300 hover:text-white hover:bg-green-500/20 transition-all duration-200"
                    title="放大"
                  >
                    ➕
                  </button>
                  <button
                    onClick={() => handleZoom('out')}
                    className="px-2 py-1 rounded text-sm text-gray-300 hover:text-white hover:bg-red-500/20 transition-all duration-200"
                    title="缩小"
                  >
                    ➖
                  </button>
                </div>
                
                {/* 搜索结果下拉框 */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900/95 backdrop-blur-sm border border-gray-600/50 rounded-lg shadow-xl max-h-64 overflow-y-auto z-50">
                    <div className="p-2">
                      <div className="text-xs text-gray-400 mb-2">
                        找到 {searchResults.length} 个匹配结果
                      </div>
                      {searchResults.map((result, index) => {
                        const nodeTypeColor = getNodeTypeColor(result.type);
                        return (
                          <button
                            key={index}
                            onClick={() => handleSearchResultSelect(result)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-700/50 active:bg-gray-600/50 transition-all duration-200 rounded-md border border-transparent hover:border-teal-500/30 mb-1 last:mb-0"
                          >
                            <div className="flex items-start gap-2">
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                                style={{ backgroundColor: nodeTypeColor }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-white text-sm truncate">{result.name}</div>
                                <div className="text-xs text-gray-400 mt-0.5 truncate">{result.pathString}</div>
                                
                                {/* 匹配类型标识 */}
                                {result.matchType && (
                                  <div className="mt-1">
                                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${
                                      result.matchType === 'nodeName' 
                                        ? 'bg-blue-500/20 text-blue-300' 
                                        : result.matchType === 'title'
                                        ? 'bg-green-500/20 text-green-300'
                                        : 'bg-yellow-500/20 text-yellow-300'
                                    }`}>
                                      {result.matchType === 'nodeName' ? '节点名称' : 
                                       result.matchType === 'title' ? '标题匹配' : '内容匹配'}
                                    </span>
                                  </div>
                                )}
                                
                                {/* 匹配上下文 */}
                                {result.matchContext && result.matchType !== 'nodeName' && (
                                  <div className="text-xs text-gray-300 bg-gray-700/50 p-2 rounded border-l-2 border-teal-500/50 mt-1">
                                    {result.matchContext.length > 100 
                                      ? result.matchContext.substring(0, 100) + '...'
                                      : result.matchContext
                                    }
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* 知识图谱主视图 */}
            <div className="flex-1 bg-black/[0.35] backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
              <div className="p-4 h-full">
                <svg 
                  ref={svgRef}
                  className="w-full h-full"
                />
              </div>
            </div>
            
            {/* 底部信息区域 */}
            <div className="mt-3 bg-black/[0.5] backdrop-blur-sm rounded-lg border border-white/10">
              {/* 当前位置指示器 */}
              <div className="px-4 py-2 border-b border-white/10">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ color: 'var(--tech-mint)' }}>📍 当前位置:</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      {currentPath.map((nodeName, index) => {
                        const nodeData = findNodeByName(nodeName);
                        const nodeColor = nodeData ? getNodeTypeColor(nodeData.type) : 'var(--ink-mid)';
                        const isClickable = nodeData;
                        
                        return (
                          <React.Fragment key={`path-${index}`}>
                            {isClickable ? (
                              <button
                                onClick={() => handlePathNodeClick(nodeName)}
                                className="hover:underline transition-all duration-200 hover:brightness-110"
                                style={{ 
                                  color: nodeColor,
                                  fontSize: 'inherit',
                                  background: 'none',
                                  border: 'none',
                                  padding: '0',
                                  cursor: 'pointer'
                                }}
                                title={`点击切换到"${nodeName}"的聚焦视图`}
                              >
                                {nodeName}
                              </button>
                            ) : (
                              <span style={{ color: nodeColor }}>
                                {nodeName}
                              </span>
                            )}
                            {index < currentPath.length - 1 && (
                              <span style={{ color: 'var(--ink-low)', margin: '0 2px' }}>›</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span style={{ color: 'var(--tech-mint)' }}>
                      {viewMode === 'overview' ? '🌐' : '🎯'} 
                      {viewMode === 'overview' ? '全局视图' : '聚焦视图'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* 节点类型图例和控制 */}
              <div className="p-3">
                <div className="flex justify-between items-center">
                  <div className="flex gap-3 text-xs flex-wrap">
                    {Object.entries({
                      root: { color: 'bg-blue-500', label: '根节点' },
                      category: { color: 'bg-green-500', label: '分类维度' },
                      framework: { color: 'bg-yellow-500', label: '框架层' },
                      subframework: { color: 'bg-purple-500', label: '子框架' },
                      method: { color: 'bg-red-500', label: '具体方法' }
                    }).map(([type, config]) => (
                      <button
                        key={type}
                        onClick={() => toggleNodeType(type)}
                        className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${
                          visibleNodeTypes[type] ? 'opacity-100' : 'opacity-50'
                        }`}
                      >
                        <div className={`w-2 h-2 ${config.color} rounded`}></div>
                        <span style={{ color: 'var(--ink-mid)' }}>{config.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="text-xs" style={{ color: 'var(--ink-mid)' }}>
                    缩放: {Math.round(zoomLevel * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：Markdown文本区域 */}
          <div className="bg-black/[0.4] backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden relative" style={{ width: '45%', marginRight: '5%' }}>
            <div className="h-full flex flex-col">
              <div className="flex-shrink-0 p-4 border-b border-white/10">
                <h3 className="text-center text-xl font-bold" 
                    style={{ 
                      color: 'var(--ink-high)',
                      fontSize: 'clamp(20px, 3vw, 28px)',
                      letterSpacing: '-0.02em'
                    }}>
                  优化问题的完整分类
                </h3>
              </div>
              <div 
                className={`flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar ${scrollbarVisible ? 'scrollbar-visible' : ''}`} 
                ref={markdownRef}
              >
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[[rehypeKatex, { displayMode: true }]]}  
                    components={{
                    p: ({node, ...props}) => (
                      <p 
                        {...props} 
                        style={{
                          color: 'var(--ink-mid)',
                          margin: '1rem 0',
                          lineHeight: '1.8'
                        }}
                      />
                    ),
                    h2: ({node, children, ...props}) => {
                      // 从标题文本中提取ID
                      const text = children?.[0];
                      let id = 'root';
                      if (typeof text === 'string' && text.includes('1.3 ')) {
                        id = 'root';
                      }
                      
                      return (
                        <h2 
                          {...props}
                          id={id}
                          style={{
                            color: 'var(--tech-mint)',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            margin: '1.5rem 0 1rem 0',
                            scrollMarginTop: '2rem'
                          }}
                        >
                          {children}
                        </h2>
                      );
                    },
                    h3: ({node, children, ...props}) => {
                      // 从标题文本中提取ID
                      const text = Array.isArray(children) ? children.join('') : children;
                      let id = '';
                      
                      if (typeof text === 'string') {
                        // 先尝试从 {#id} 语法中提取
                        const idMatch = text.match(/\{#([^}]+)\}/);
                        if (idMatch) {
                          id = idMatch[1];
                        } else {
                          // 如果没有 {#id} 语法，根据内容匹配
                          if (text.includes('按决策变量类型')) id = 'decision-variable-type';
                          else if (text.includes('按约束与可行域结构')) id = 'constraint-feasible-region';
                          else if (text.includes('按目标与模型结构')) id = 'objective-model-structure';
                          else if (text.includes('按不确定性与信息结构')) id = 'uncertainty-information';
                          else if (text.includes('按时域与决策流程')) id = 'time-decision-process';
                          else if (text.includes('按目标个数与解的概念')) id = 'objective-count-solution';
                          else if (text.includes('按求解策略与算法范式')) id = 'solving-strategy-algorithm';
                          else if (text.includes('按规模与结构可利用性')) id = 'scale-structure-utility';
                        }
                      }
                      
                      // 清理标题文本，移除 {#id} 部分
                      const cleanText = typeof text === 'string' ? text.replace(/\s*\{#[^}]+\}/g, '') : text;
                      
                      return (
                        <h3 
                          {...props}
                          id={id}
                          style={{
                            color: 'var(--ink-high)',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            margin: '1.25rem 0 0.75rem 0',
                            scrollMarginTop: '2rem'
                          }}
                        >
                          {cleanText}
                        </h3>
                      );
                    },
                    h4: ({node, children, ...props}) => {
                      // 从标题文本中提取ID
                      const text = Array.isArray(children) ? children.join('') : children;
                      let id = '';
                      
                      if (typeof text === 'string') {
                        // 先尝试从 {#id} 语法中提取
                        const idMatch = text.match(/\{#([^}]+)\}/);
                        if (idMatch) {
                          id = idMatch[1];
                        }
                      }
                      
                      // 清理标题文本，移除 {#id} 部分
                      const cleanText = typeof text === 'string' ? text.replace(/\s*\{#[^}]+\}/g, '') : text;
                      
                      return (
                        <h4 
                          {...props}
                          id={id}
                          style={{
                            color: 'var(--ink-high)',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            margin: '1rem 0 0.5rem 0',
                            scrollMarginTop: '2rem'
                          }}
                        >
                          {cleanText}
                        </h4>
                      );
                    },
                    h5: ({node, children, ...props}) => {
                      // 从标题文本中提取ID
                      const text = Array.isArray(children) ? children.join('') : children;
                      let id = '';
                      
                      if (typeof text === 'string') {
                        // 先尝试从 {#id} 语法中提取
                        const idMatch = text.match(/\{#([^}]+)\}/);
                        if (idMatch) {
                          id = idMatch[1];
                        }
                      }
                      
                      // 清理标题文本，移除 {#id} 部分
                      const cleanText = typeof text === 'string' ? text.replace(/\s*\{#[^}]+\}/g, '') : text;
                      
                      return (
                        <h5 
                          {...props}
                          id={id}
                          style={{
                            color: 'var(--ink-high)',
                            fontSize: '1rem',
                            fontWeight: '600',
                            margin: '0.875rem 0 0.375rem 0',
                            scrollMarginTop: '2rem'
                          }}
                        >
                          {cleanText}
                        </h5>
                      );
                    },
                    strong: ({node, ...props}) => (
                      <strong 
                        {...props} 
                        style={{
                          color: 'var(--ink-high)',
                          fontWeight: '600'
                        }}
                      />
                    ),
                    em: ({node, ...props}) => (
                      <em 
                        {...props} 
                        style={{
                          color: 'var(--tech-mint)',
                          fontStyle: 'italic'
                        }}
                      />
                    ),
                    ul: ({node, ...props}) => (
                      <ul 
                        {...props} 
                        style={{
                          color: 'var(--ink-mid)',
                          margin: '0.75rem 0',
                          paddingLeft: '1.5rem',
                          listStyleType: 'disc'
                        }}
                      />
                    ),
                    ol: ({node, ...props}) => (
                      <ol 
                        {...props} 
                        style={{
                          color: 'var(--ink-mid)',
                          margin: '0.75rem 0',
                          paddingLeft: '1.5rem',
                          listStyleType: 'decimal'
                        }}
                      />
                    ),
                    li: ({node, ...props}) => (
                      <li 
                        {...props} 
                        style={{
                          color: 'var(--ink-mid)',
                          margin: '0.5rem 0',
                          lineHeight: '1.8'
                        }}
                      />
                    ),
                    blockquote: ({node, ...props}) => (
                      <blockquote 
                        {...props} 
                        style={{
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          borderLeft: '4px solid var(--tech-mint)',
                          margin: '1rem 0',
                          padding: '0.75rem 1rem',
                          borderRadius: '0 0.5rem 0.5rem 0',
                          fontStyle: 'italic'
                        }}
                      />
                    ),
                    pre: ({node, ...props}) => (
                      <pre 
                        {...props} 
                        style={{
                          backgroundColor: 'rgba(30, 41, 59, 0.8)',
                          color: 'var(--ink-high)',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          margin: '1rem 0',
                          overflow: 'auto',
                          fontSize: '0.9em',
                          lineHeight: '1.4'
                        }}
                      />
                    ),
                    a: ({node, href, children, ...props}) => (
                      <a 
                        {...props}
                        href={href}
                        target={href && href.startsWith('http') ? '_blank' : undefined}
                        rel={href && href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        style={{
                          color: 'var(--tech-mint)',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#10b981';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'var(--tech-mint)';
                        }}
                      >
                        {children}
                      </a>
                    ),
                    }}
                  >
                    {fullMarkdownContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      
      <div className="absolute bottom-0 left-0 right-0 z-50">
        <button
          onClick={() => {
            const target = document.getElementById('concept-3');
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="absolute bottom-8 left-1/2 flex flex-col items-center gap-2 
                     transition-colors duration-300 group"
          style={{
            transform: 'translateX(-50%)',
            color: 'var(--ink-mid)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--tech-mint)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--ink-mid)';
          }}
        >
          <span className="text-sm">向下滚动继续</span>
          <svg 
            className="w-6 h-6 animate-bounce"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      </div>
      
      {/* 呼吸发光动画样式 */}
      <style>{`
        /* 高亮连线呼吸发光动画 */
        :global(.focus-link-breathing) {
          animation: linkBreathing 2.5s ease-in-out infinite;
        }
        
        @keyframes linkBreathing {
          0%, 100% {
            opacity: 0.6;
            stroke-width: 2px;
          }
          50% {
            opacity: 0.9;
            stroke-width: 2.5px;
          }
        }
      `}</style>
      </section>
    </>
  );
};

export const meta = {
  id: 3,
  title: '分类导图',
  summary: '四主干分类/测绘案例/导航跳转',
  anchor: 'concept-3',
};

export default Section3Mindmap;